import React, { useState, useRef } from 'react';
import { Link, useLocation } from 'wouter';
import MentorshipAccessGate from '@/components/MentorshipAccessGate';
import { ArrowLeft, FileText, Book, Lightbulb, Info, ChevronLeft, ChevronRight, Play, Maximize, Minimize, X } from 'lucide-react';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";

// Simple KeyTakeaway component
const KeyTakeaway = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="bg-blue-50 border-l-4 border-blue-500 rounded-r-md p-5 my-6">
      <div className="flex">
        <Lightbulb className="text-blue-500 h-6 w-6 mr-3 flex-shrink-0 mt-0.5" />
        <div className="text-blue-700 font-serif">
          {children}
        </div>
      </div>
    </div>
  );
};

// Slide interface
interface Slide {
  id: number;
  title: string;
  content: React.ReactNode;
  image?: string;
}

// Slideshow component
const Slideshow = ({ slides }: { slides: Slide[] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const slideshowRef = useRef<HTMLDivElement>(null);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev === slides.length - 1 ? 0 : prev + 1));
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev === 0 ? slides.length - 1 : prev - 1));
  };
  
  const toggleFullscreen = () => {
    if (!isFullscreen) {
      // Enter fullscreen
      if (slideshowRef.current?.requestFullscreen) {
        slideshowRef.current.requestFullscreen().catch(err => {
          console.error(`Error attempting to enable fullscreen: ${err.message}`);
        });
      }
    } else {
      // Exit fullscreen
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(err => {
          console.error(`Error attempting to exit fullscreen: ${err.message}`);
        });
      }
    }
  };
  
  // Add event listeners for fullscreen change
  React.useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, []);

  if (slides.length === 0) return null;

  return (
    <div 
      ref={slideshowRef}
      className={`relative ${isFullscreen ? 'fixed inset-0 z-50 flex items-center justify-center bg-white' : 'bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl overflow-hidden mb-10'}`}
    >
      <div className={`${isFullscreen ? 'px-10 py-20 w-full h-full flex items-center justify-center' : 'px-6 py-12 md:px-12 md:py-16'}`}>
        <div className={`${isFullscreen ? 'max-w-6xl w-full' : 'max-w-4xl mx-auto'}`}>
          <div className="space-y-6">
            <h2 className={`${isFullscreen ? 'text-3xl md:text-4xl text-blue-900' : 'text-2xl md:text-3xl text-blue-900'} font-serif text-center font-bold`}>
              {slides[currentSlide].title}
            </h2>
            <div className={`prose prose-lg max-w-none ${isFullscreen ? 'prose-strong:text-blue-700' : 'prose-strong:text-blue-700'} overflow-y-auto ${isFullscreen ? 'max-h-[calc(100vh-180px)]' : 'max-h-[60vh]'}`} style={{scrollbarWidth: 'thin'}}>
              {slides[currentSlide].content}
            </div>
            {slides[currentSlide].image && (
              <div className="mt-6 flex justify-center">
                <div className={`${isFullscreen ? 'bg-gray-50 p-3' : 'bg-white p-2'} rounded-lg shadow-lg`}>
                  <img 
                    src={slides[currentSlide].image} 
                    alt={slides[currentSlide].title}
                    className={`${isFullscreen ? 'max-h-[500px]' : 'max-h-[300px]'} object-contain rounded`}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {isFullscreen && (
        <button
          onClick={toggleFullscreen}
          className="absolute top-4 right-4 text-gray-500 bg-gray-100 p-2 rounded-full hover:bg-gray-200"
        >
          <X className="h-6 w-6" />
        </button>
      )}

      <div className="absolute bottom-4 right-4 flex space-x-2">
        <Button
          size="sm"
          variant="outline"
          className={`${isFullscreen ? 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200' : 'bg-white/80 hover:bg-white'}`}
          onClick={prevSlide}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          className={`${isFullscreen ? 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200' : 'bg-white/80 hover:bg-white'}`}
          onClick={nextSlide}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          size="sm"
          variant="outline"
          className={`${isFullscreen ? 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200' : 'bg-white/80 hover:bg-white'}`}
          onClick={toggleFullscreen}
        >
          {isFullscreen ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
        </Button>
      </div>

      <div className="absolute bottom-4 left-4">
        <span className={`text-sm font-medium ${isFullscreen ? 'bg-gray-100 text-gray-700' : 'bg-white/80'} px-2 py-1 rounded`}>
          {currentSlide + 1} / {slides.length}
        </span>
      </div>
    </div>
  );
};

// Video component with embedded Descript video
const VideoPlayer = () => {
  return (
    <div className="w-full aspect-video rounded-lg overflow-hidden my-10">
      <iframe 
        src="https://share.descript.com/embed/XKvFdwrq8pm" 
        width="100%" 
        height="100%" 
        frameBorder="0" 
        allowFullScreen
        className="rounded-lg"
      ></iframe>
    </div>
  );
};

export default function CortisolMolecularFoundations() {
  const [_, navigate] = useLocation();
  
  // Lecture slides data
  const lectureSlides: Slide[] = [
    {
      id: 1,
      title: "Cortisol: Molecular Overview",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-xl font-medium text-blue-900 mb-2">Key Characteristics</h3>
              <ul className="space-y-2 list-disc list-inside">
                <li>Primary endogenous glucocorticoid in humans</li>
                <li>Steroid hormone with four-ring structure</li>
                <li>Formula: C<sub>21</sub>H<sub>30</sub>O<sub>5</sub></li>
                <li>Synthesized in zona fasciculata of adrenal cortex</li>
                <li>Nearly ubiquitous receptor expression across tissues</li>
              </ul>
              
              <div className="mt-4 bg-blue-50 p-3 rounded-md border-l-4 border-blue-500">
                <p className="text-sm font-medium">
                  "The 'stress hormone' that influences virtually every organ system with broad physiological impact on metabolism, immunity, cardiovascular function, and cognition."
                </p>
              </div>
            </div>
            
            <div className="flex flex-col justify-between">
              <div className="bg-white p-3 rounded-lg shadow-md mb-3">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="border-b-2 border-gray-200 text-left pb-2">Function</th>
                      <th className="border-b-2 border-gray-200 text-left pb-2">Primary Effect</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100">Metabolic</td>
                      <td className="py-2 border-b border-gray-100">↑ Gluconeogenesis</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100">Immune</td>
                      <td className="py-2 border-b border-gray-100">↓ Inflammation</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100">Cardiovascular</td>
                      <td className="py-2 border-b border-gray-100">↑ Vascular tone</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100">Neurological</td>
                      <td className="py-2 border-b border-gray-100">Modulates cognition</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2">Tissue</td>
                      <td className="py-2">Primarily catabolic</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="text-xs mt-1 text-gray-600 italic">
                <span className="font-medium">Clinical relevance:</span> Excess → Cushing's syndrome; 
                Deficiency → Addison's disease
              </div>
            </div>
          </div>
        </>
      ),
      image: "https://www.ncbi.nlm.nih.gov/structure/imgsrv.fcgi?uid=CID_5754&t=l"
    },
    {
      id: 2,
      title: "Cortisol Biosynthesis Pathway",
      content: (
        <>
          <div className="bg-white rounded-lg p-4 shadow-md">
            <div className="text-center mb-3">
              <h3 className="text-blue-900 font-medium">Steroidogenic Pathway in Adrenal Cortex</h3>
            </div>
            
            <div className="relative py-2 overflow-visible">
              {/* Enzymatic cascade with arrows */}
              <div className="flex flex-col space-y-10">
                {/* Step 1 */}
                <div className="flex items-center">
                  <div className="w-1/3 text-right pr-4">
                    <div className="font-mono bg-yellow-50 p-2 rounded-md inline-block">
                      Cholesterol
                    </div>
                  </div>
                  <div className="w-1/3 flex justify-center items-center">
                    <div className="flex flex-col items-center">
                      <div className="h-8 border-l-2 border-dashed border-gray-400"></div>
                      <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                        P450scc (CYP11A1)
                      </div>
                      <div className="h-8 border-l-2 border-dashed border-gray-400"></div>
                    </div>
                  </div>
                  <div className="w-1/3"></div>
                </div>
                
                {/* Step 2 */}
                <div className="flex items-center">
                  <div className="w-1/3 text-right pr-4">
                    <div className="font-mono bg-yellow-50 p-2 rounded-md inline-block">
                      Pregnenolone
                    </div>
                  </div>
                  <div className="w-1/3 flex justify-center items-center">
                    <div className="flex flex-col items-center">
                      <div className="h-8 border-l-2 border-dashed border-gray-400"></div>
                      <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                        3β-HSD
                      </div>
                      <div className="h-8 border-l-2 border-dashed border-gray-400"></div>
                    </div>
                  </div>
                  <div className="w-1/3"></div>
                </div>
                
                {/* Step 3 */}
                <div className="flex items-center">
                  <div className="w-1/3 text-right pr-4">
                    <div className="font-mono bg-yellow-50 p-2 rounded-md inline-block">
                      Progesterone
                    </div>
                  </div>
                  <div className="w-1/3 flex justify-center items-center">
                    <div className="flex flex-col items-center">
                      <div className="h-8 border-l-2 border-dashed border-gray-400"></div>
                      <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                        17α-hydroxylase (CYP17A1)
                      </div>
                      <div className="h-8 border-l-2 border-dashed border-gray-400"></div>
                    </div>
                  </div>
                  <div className="w-1/3"></div>
                </div>
                
                {/* Step 4 */}
                <div className="flex items-center">
                  <div className="w-1/3 text-right pr-4">
                    <div className="font-mono bg-yellow-50 p-2 rounded-md inline-block">
                      17α-OH-Progesterone
                    </div>
                  </div>
                  <div className="w-1/3 flex justify-center items-center">
                    <div className="flex flex-col items-center">
                      <div className="h-8 border-l-2 border-dashed border-gray-400"></div>
                      <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                        21-hydroxylase (CYP21A2)
                      </div>
                      <div className="h-8 border-l-2 border-dashed border-gray-400"></div>
                    </div>
                  </div>
                  <div className="w-1/3"></div>
                </div>
                
                {/* Step 5 */}
                <div className="flex items-center">
                  <div className="w-1/3 text-right pr-4">
                    <div className="font-mono bg-yellow-50 p-2 rounded-md inline-block">
                      11-Deoxycortisol
                    </div>
                  </div>
                  <div className="w-1/3 flex justify-center items-center">
                    <div className="flex flex-col items-center">
                      <div className="h-8 border-l-2 border-dashed border-gray-400"></div>
                      <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                        11β-hydroxylase (CYP11B1)
                      </div>
                      <div className="h-8 border-l-2 border-dashed border-gray-400"></div>
                    </div>
                  </div>
                  <div className="w-1/3"></div>
                </div>
                
                {/* Final product */}
                <div className="flex items-center">
                  <div className="w-1/3 text-right pr-4">
                    <div className="font-mono bg-green-100 p-2 border-2 border-green-500 rounded-md inline-block">
                      Cortisol
                    </div>
                  </div>
                  <div className="w-1/3"></div>
                  <div className="w-1/3"></div>
                </div>
              </div>
            </div>
            
            <div className="mt-2 text-xs text-gray-600 italic">
              <span className="font-medium">Note:</span> Deficiencies in these enzymes cause various forms of congenital adrenal hyperplasia (CAH)
            </div>
          </div>
        </>
      )
    },
    {
      id: 3,
      title: "HPA Axis: Regulation and Feedback",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-2">Cascade Mechanism</h3>
              <ol className="space-y-3 list-decimal pl-4">
                <li>
                  <strong>Hypothalamus</strong> releases corticotropin-releasing hormone (CRH) in response to stress, circadian signals, or inflammation
                </li>
                <li>
                  <strong>Pituitary gland</strong> secretes adrenocorticotropic hormone (ACTH) in response to CRH
                </li>
                <li>
                  <strong>Adrenal cortex</strong> synthesizes and releases cortisol when stimulated by ACTH
                </li>
                <li>
                  <strong>Negative feedback</strong>: Cortisol inhibits further CRH and ACTH release at the hypothalamus and pituitary
                </li>
              </ol>
              
              <div className="bg-amber-50 p-3 mt-4 rounded-md border border-amber-200">
                <h4 className="text-amber-800 font-medium text-sm">Clinical Application</h4>
                <p className="text-sm mt-1">
                  Dexamethasone suppression test (DST) exploits negative feedback. Abnormal results may indicate Cushing's syndrome or psychiatric conditions like severe depression.
                </p>
              </div>
            </div>
            
            <div>
              <div className="relative bg-white rounded-lg p-3 shadow-md">
                <div className="flex flex-col items-center">
                  {/* Hypothalamus */}
                  <div className="bg-indigo-100 p-3 rounded-md w-48 text-center mb-2">
                    <strong className="text-indigo-800">Hypothalamus</strong>
                    <div className="text-xs mt-1">Secretes CRH</div>
                  </div>
                  
                  {/* Arrow down */}
                  <div className="h-6 border-l-2 border-gray-400 relative">
                    <div className="absolute -right-2 top-4 text-xs">CRH</div>
                  </div>
                  
                  {/* Pituitary */}
                  <div className="bg-blue-100 p-3 rounded-md w-48 text-center mb-2">
                    <strong className="text-blue-800">Anterior Pituitary</strong>
                    <div className="text-xs mt-1">Secretes ACTH</div>
                  </div>
                  
                  {/* Arrow down */}
                  <div className="h-6 border-l-2 border-gray-400 relative">
                    <div className="absolute -right-3 top-4 text-xs">ACTH</div>
                  </div>
                  
                  {/* Adrenal */}
                  <div className="bg-green-100 p-3 rounded-md w-48 text-center">
                    <strong className="text-green-800">Adrenal Cortex</strong>
                    <div className="text-xs mt-1">Secretes Cortisol</div>
                  </div>
                  
                  {/* Feedback loop - arrow up */}
                  <div className="absolute right-12 h-full border-r-2 border-red-400 border-dashed">
                    <div className="absolute -left-16 top-1/2 transform -translate-y-1/2 text-xs text-red-600">
                      Negative Feedback
                    </div>
                    <div className="absolute left-2 top-0 text-red-600">⊝</div>
                    <div className="absolute left-2 top-12 text-red-600">⊝</div>
                  </div>
                </div>
              </div>
              
              <div className="mt-3 text-sm">
                <strong>Activators:</strong> Physical/psychological stress, hypoglycemia, inflammatory cytokines
              </div>
              <div className="mt-1 text-sm">
                <strong>Inhibitors:</strong> Glucocorticoids, opioids, chronic stress adaptation
              </div>
            </div>
          </div>
        </>
      ),
      image: "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1c/Hypothalamic_pituitary_adrenal_axis.svg/1024px-Hypothalamic_pituitary_adrenal_axis.svg.png"
    },
    {
      id: 4,
      title: "Circadian Rhythms and Cortisol Pulsatility",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">Diurnal Pattern</h3>
              
              <div className="bg-white p-3 rounded-lg shadow-md">
                <div className="relative h-48 border-b border-l border-gray-300">
                  {/* X axis labels */}
                  <div className="absolute bottom-0 left-0 w-full flex justify-between text-xs px-2">
                    <div>0:00</div>
                    <div>6:00</div>
                    <div>12:00</div>
                    <div>18:00</div>
                    <div>24:00</div>
                  </div>
                  
                  {/* Y axis label */}
                  <div className="absolute -left-6 top-1/2 transform -translate-y-1/2 -rotate-90 text-xs whitespace-nowrap">
                    Cortisol Level
                  </div>
                  
                  {/* Cortisol curve */}
                  <svg viewBox="0 0 100 48" className="absolute inset-0 h-full w-full">
                    <path 
                      d="M0,45 C5,42 10,30 20,10 C25,5 30,8 40,15 C50,22 60,28 70,36 C80,42 90,44 100,45" 
                      fill="none" 
                      stroke="#3B82F6" 
                      strokeWidth="2"
                    />
                  </svg>
                  
                  {/* Cortisol Awakening Response */}
                  <div className="absolute top-2 left-20 text-xs">
                    <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                      Morning Cortisol Peak
                    </div>
                    <div className="border-l border-dashed border-blue-400 h-6 ml-6"></div>
                  </div>
                  
                  {/* Evening Nadir */}
                  <div className="absolute bottom-8 right-8 text-xs">
                    <div className="border-l border-dashed border-blue-400 h-6 ml-6"></div>
                    <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                      Evening Nadir
                    </div>
                  </div>
                </div>
                
                <div className="mt-2 text-xs text-center">
                  <span className="font-medium">Normal Pattern:</span> Peak at awakening; gradual decline; nadir at midnight
                </div>
              </div>
            </div>
            
            <div>
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-medium text-blue-900 mb-2">Key Features</h3>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <div className="bg-amber-100 text-amber-800 p-1 rounded-full mr-2 text-xs font-bold mt-0.5">1</div>
                      <div>
                        <strong>Morning Peak (CAR)</strong>
                        <p className="text-sm">Rise 50-75% within 30-45 min after waking, preparing body for daytime activities</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-amber-100 text-amber-800 p-1 rounded-full mr-2 text-xs font-bold mt-0.5">2</div>
                      <div>
                        <strong>Ultradian Pulses</strong>
                        <p className="text-sm">1-2 hour frequency oscillations throughout the day</p>
                      </div>
                    </li>
                    <li className="flex items-start">
                      <div className="bg-amber-100 text-amber-800 p-1 rounded-full mr-2 text-xs font-bold mt-0.5">3</div>
                      <div>
                        <strong>Evening Nadir</strong>
                        <p className="text-sm">Low levels permit sleep onset and tissue repair</p>
                      </div>
                    </li>
                  </ul>
                </div>
                
                <div className="bg-red-50 p-3 rounded-md border-l-4 border-red-400">
                  <h4 className="text-red-800 font-medium">Clinical Implications</h4>
                  <ul className="mt-1 space-y-1 text-sm">
                    <li>Shift work → Inverted rhythm → Metabolic risk</li>
                    <li>Aging → Flattened curve → Cognitive effects</li>
                    <li>Chronic stress → Elevated evening cortisol → Sleep disruption</li>
                    <li>Flattened rhythm → Associated with chronic fatigue, depression</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 5,
      title: "Cortisol Receptor Interactions",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">Glucocorticoid Receptor (GR)</h3>
              
              <div className="bg-white rounded-lg p-3 shadow-md">
                <div className="relative py-2">
                  {/* Cell diagram */}
                  <div className="relative border-2 border-gray-300 rounded-lg p-4 h-80">
                    {/* Cell membrane label */}
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-white px-2 text-xs">
                      Cell Membrane
                    </div>
                    
                    {/* Cytoplasm label */}
                    <div className="absolute top-6 left-2 text-xs text-gray-500">Cytoplasm</div>
                    
                    {/* Nuclear membrane */}
                    <div className="absolute top-1/3 left-1/4 right-1/4 bottom-1/6 border-2 border-gray-300 rounded-full bg-gray-50">
                      {/* Nucleus label */}
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 text-xs text-gray-500">
                        Nucleus
                      </div>
                    </div>
                    
                    {/* Cortisol entering */}
                    <div className="absolute top-12 -left-1">
                      <div className="flex items-center">
                        <div className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs">
                          Cortisol
                        </div>
                        <div className="w-8 h-0.5 bg-yellow-400"></div>
                      </div>
                    </div>
                    
                    {/* GR in cytoplasm */}
                    <div className="absolute top-24 left-16">
                      <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                        GR + chaperones
                      </div>
                    </div>
                    
                    {/* Arrow from cortisol to GR */}
                    <svg className="absolute top-16 left-16" width="40" height="25">
                      <defs>
                        <marker id="arrowhead" markerWidth="5" markerHeight="5" refX="5" refY="2.5" orient="auto">
                          <polygon points="0 0, 5 2.5, 0 5" fill="#9333EA" />
                        </marker>
                      </defs>
                      <path d="M0,0 L35,20" stroke="#9333EA" strokeWidth="1.5" markerEnd="url(#arrowhead)" fill="none" />
                    </svg>
                    
                    {/* Complex to nucleus */}
                    <div className="absolute top-40 left-28">
                      <div className="flex items-center">
                        <div className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs">
                          Cortisol-GR
                        </div>
                      </div>
                    </div>
                    
                    {/* Arrow to nucleus */}
                    <svg className="absolute top-44 left-40" width="25" height="25">
                      <defs>
                        <marker id="arrowhead2" markerWidth="5" markerHeight="5" refX="5" refY="2.5" orient="auto">
                          <polygon points="0 0, 5 2.5, 0 5" fill="#9333EA" />
                        </marker>
                      </defs>
                      <path d="M0,0 L20,20" stroke="#9333EA" strokeWidth="1.5" markerEnd="url(#arrowhead2)" fill="none" />
                    </svg>
                    
                    {/* DNA binding */}
                    <div className="absolute bottom-10 right-1/4 left-1/3">
                      <div className="bg-indigo-100 text-indigo-800 px-2 py-1 rounded-full text-xs whitespace-nowrap">
                        GRE binding → Gene expression
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-1 text-xs">
                  <strong>Mechanism:</strong> Cortisol diffuses into cell → binds GR → complex enters nucleus → 
                  regulates gene transcription
                </div>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-medium text-blue-900 mb-2">Receptor Comparison</h3>
                <div className="bg-white rounded-lg p-3 shadow-md">
                  <table className="w-full text-sm">
                    <thead>
                      <tr>
                        <th className="border-b-2 border-gray-200 text-left pb-2">Feature</th>
                        <th className="border-b-2 border-gray-200 text-left pb-2">Glucocorticoid Receptor (GR)</th>
                        <th className="border-b-2 border-gray-200 text-left pb-2">Mineralocorticoid Receptor (MR)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td className="py-2 pr-2 border-b border-gray-100 font-medium">Gene</td>
                        <td className="py-2 border-b border-gray-100">NR3C1</td>
                        <td className="py-2 border-b border-gray-100">NR3C2</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-2 border-b border-gray-100 font-medium">Affinity for cortisol</td>
                        <td className="py-2 border-b border-gray-100">Moderate</td>
                        <td className="py-2 border-b border-gray-100">High (10× higher than GR)</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-2 border-b border-gray-100 font-medium">Distribution</td>
                        <td className="py-2 border-b border-gray-100">Nearly ubiquitous</td>
                        <td className="py-2 border-b border-gray-100">Kidney, brain, heart, colon</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-2 border-b border-gray-100 font-medium">Protected by 11β-HSD2</td>
                        <td className="py-2 border-b border-gray-100">No</td>
                        <td className="py-2 border-b border-gray-100">Yes (in kidney, colon)</td>
                      </tr>
                      <tr>
                        <td className="py-2 pr-2 font-medium">Primary functions</td>
                        <td className="py-2">Metabolism, immunity, stress response</td>
                        <td className="py-2">Electrolyte balance, blood pressure regulation</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
              
              <div className="bg-green-50 p-3 rounded-md border-l-4 border-green-400">
                <h4 className="text-green-800 font-medium">Clinical Applications</h4>
                <ul className="mt-1 space-y-1 text-sm">
                  <li><strong>Glucocorticoid drugs</strong> (prednisone, dexamethasone) target GR</li>
                  <li><strong>11β-HSD2 inhibition</strong> (e.g., licorice consumption) → cortisol acts on MR → hypertension</li>
                  <li><strong>GR polymorphisms</strong> → variability in therapeutic response</li>
                  <li><strong>GR resistance</strong> → observed in inflammatory conditions, some psychiatric disorders</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 6,
      title: "Cortisol-Testosterone Interplay",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">Anabolic vs. Catabolic Balance</h3>
              
              <div className="bg-white p-3 rounded-lg shadow-sm mb-4 border-l-4 border-blue-400">
                <p className="text-sm">
                  Cortisol and testosterone often function as physiological antagonists, creating a functional tug-of-war at the cellular level that balances catabolic (breakdown) and anabolic (building) processes.
                </p>
              </div>
              
              <div className="space-y-3">
                <div className="bg-gradient-to-r from-red-50 to-red-100 p-3 rounded-md">
                  <h4 className="text-sm font-medium text-red-800">Cortisol (Catabolic)</h4>
                  <ul className="text-xs mt-1 space-y-1 list-disc list-inside text-red-700">
                    <li>Promotes protein breakdown</li>
                    <li>Increases fat storage</li>
                    <li>Reduces muscle protein synthesis</li>
                    <li>Suppresses testosterone production</li>
                  </ul>
                </div>
                
                <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-md">
                  <h4 className="text-sm font-medium text-blue-800">Testosterone (Anabolic)</h4>
                  <ul className="text-xs mt-1 space-y-1 list-disc list-inside text-blue-700">
                    <li>Stimulates protein synthesis</li>
                    <li>Promotes muscle hypertrophy</li>
                    <li>Decreases fat mass</li>
                    <li>May help regulate HPA axis activity</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-amber-50 p-3 mt-4 rounded-md border border-amber-200">
                <h4 className="text-amber-800 font-medium text-sm">Clinical Significance</h4>
                <p className="text-xs mt-1">
                  A favorable testosterone-to-cortisol ratio (T/C ratio) is associated with anabolism and better recovery in athletes. Chronic stress can disrupt this balance, leading to decreased performance, reduced muscle mass, and impaired recovery.
                </p>
              </div>
            </div>
            
            <div>
              <div className="bg-white p-4 rounded-lg shadow-md">
                <h4 className="text-sm font-medium text-center text-gray-700 mb-4">Reciprocal Regulation Mechanisms</h4>
                
                <div className="grid grid-rows-[auto_auto_auto] gap-y-4 h-auto">
                  {/* Top row - Hormone regulation */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Left column - HPA axis */}
                    <div className="flex flex-col items-center">
                      <div className="bg-indigo-100 p-2 rounded text-xs font-medium text-center w-24">
                        Stress
                      </div>
                      <svg className="h-5 w-5 text-gray-500 my-1" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5L12 19" stroke="currentColor" strokeWidth="2"/>
                        <path d="M19 12L12 19L5 12" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      <div className="bg-blue-100 p-2 rounded text-xs font-medium text-center w-28">
                        HPA Activation
                      </div>
                      <svg className="h-5 w-5 text-gray-500 my-1" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5L12 19" stroke="currentColor" strokeWidth="2"/>
                        <path d="M19 12L12 19L5 12" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                      <div className="bg-red-100 p-2 rounded text-xs font-medium text-center w-28">
                        ↑ Cortisol
                      </div>
                    </div>
                    
                    {/* Right column - HPG axis */}
                    <div className="flex flex-col items-center">
                      <div className="bg-green-100 p-2 rounded text-xs font-medium text-center w-28">
                        HPG Axis
                      </div>
                      <div className="text-center mt-1 mb-2">
                        <span className="text-xs">(GnRH → LH → Testes)</span>
                      </div>
                      <div className="bg-blue-100 p-2 rounded text-xs font-medium text-center w-28">
                        ↑ Testosterone
                      </div>
                    </div>
                  </div>
                  
                  {/* Middle row - Receptor competition box */}
                  <div className="w-full flex justify-center mt-2">
                    <div className="bg-purple-50 p-2 rounded-md border border-purple-200 w-[80%] text-center">
                      <div className="text-xs font-medium text-purple-800">Receptor Level Competition</div>
                      <p className="text-[10px] mt-1 text-gray-700">
                        Both hormones compete for binding sites and influence each other's receptor signaling pathways in tissues like muscle and fat.
                      </p>
                    </div>
                  </div>
                  
                  {/* Bottom row - Tissue effects */}
                  <div className="grid grid-cols-2 gap-4 mt-2">
                    <div className="bg-red-50 p-3 rounded-md border border-red-200">
                      <h5 className="text-xs font-medium text-red-800 mb-1">Cortisol Effects</h5>
                      <ul className="text-[10px] list-disc list-inside text-red-700">
                        <li>Activates ubiquitin-proteasome system</li>
                        <li>↑ REDD1, KLF15 expression</li>
                        <li>↓ mTOR pathway activity</li>
                      </ul>
                    </div>
                    
                    <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
                      <h5 className="text-xs font-medium text-blue-800 mb-1">Testosterone Effects</h5>
                      <ul className="text-[10px] list-disc list-inside text-blue-700">
                        <li>↑ mTOR pathway activation</li>
                        <li>↑ Satellite cell activation</li>
                        <li>↓ Muscle protein breakdown</li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* Inhibition arrow - rendered separately on top */}
                  <div className="absolute top-[115px] left-[45%] w-[15%] border-t-2 border-dashed border-red-400">
                    <div className="absolute -right-2 -top-2 text-red-500 text-xl">⊝</div>
                    <div className="absolute top-[-20px] left-1/2 transform -translate-x-1/2 text-xs text-red-600">
                      Inhibits
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="text-xs mt-3 text-gray-600 italic">
                <span className="font-medium">Note:</span> In chronic stress states, persistently elevated cortisol can suppress testosterone production, creating an unfavorable anabolic/catabolic balance that affects muscle maintenance and metabolic health.
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 7,
      title: "Receptor Signaling Cross-Talk",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">Shared Receptor Mechanisms</h3>
              
              <div className="bg-white rounded-lg p-3 shadow-md mb-4">
                <div className="text-center mb-2">
                  <h4 className="text-sm font-medium text-gray-800">Nuclear Receptor Superfamily</h4>
                </div>
                
                <div className="flex items-center justify-center space-x-8 mb-3">
                  <div className="flex flex-col items-center">
                    <div className="h-12 w-12 rounded-full bg-red-100 border-2 border-red-300 flex items-center justify-center">
                      <span className="text-sm font-bold text-red-600">GR</span>
                    </div>
                    <div className="text-xs mt-1 text-center">Glucocorticoid<br/>Receptor</div>
                  </div>
                  
                  <div className="flex flex-col items-center">
                    <div className="h-12 w-12 rounded-full bg-blue-100 border-2 border-blue-300 flex items-center justify-center">
                      <span className="text-sm font-bold text-blue-600">AR</span>
                    </div>
                    <div className="text-xs mt-1 text-center">Androgen<br/>Receptor</div>
                  </div>
                </div>
                
                <div className="bg-gray-50 p-2 rounded-md">
                  <div className="text-xs text-center text-gray-700">
                    <strong>Shared Features</strong>: DNA-binding domains, steroid-binding domains, and heat shock protein interactions
                  </div>
                </div>
              </div>
              
              <div className="bg-gray-50 p-3 rounded-md border border-gray-200">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Competitive Binding Effects</h4>
                <ul className="text-xs space-y-2">
                  <li className="flex items-start">
                    <div className="h-4 w-4 rounded-full bg-red-100 border border-red-300 flex-shrink-0 flex items-center justify-center mr-2 mt-0.5">
                      <span className="text-[8px] font-bold text-red-600">GR</span>
                    </div>
                    <span>
                      <strong>Desensitization:</strong> Testosterone or DHT can decrease GR expression in muscle, reducing tissue sensitivity to cortisol's catabolic signals
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="h-4 w-4 rounded-full bg-blue-100 border border-blue-300 flex-shrink-0 flex items-center justify-center mr-2 mt-0.5">
                      <span className="text-[8px] font-bold text-blue-600">AR</span>
                    </div>
                    <span>
                      <strong>Genomic overlap:</strong> AR and GR bind to overlapping regulatory regions of certain genes, allowing transcriptional cross-talk
                    </span>
                  </li>
                </ul>
              </div>
              
              <div className="text-xs mt-4 bg-amber-50 p-2 rounded-md border-l-4 border-amber-300">
                <strong>Research finding:</strong> Resistance training can reduce the affinity of muscle androgen receptors for glucocorticoids, potentially protecting against cortisol-induced muscle protein breakdown.
              </div>
            </div>
            
            <div>
              <div className="bg-white p-4 rounded-lg shadow-md mb-4">
                <h4 className="text-sm font-medium text-blue-900 mb-3">Intracellular Signaling Pathways</h4>
                
                <div className="relative h-[260px]">
                  {/* Cell membrane */}
                  <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-r from-orange-100 to-yellow-100 rounded-t-lg border-b border-orange-200"></div>
                  
                  {/* Common signaling pathways */}
                  <div className="absolute top-8 left-0 right-0 flex justify-between px-4">
                    <div className="w-[45%] flex flex-col items-center">
                      <div className="p-2 rounded-full bg-red-100 text-xs font-medium text-red-800 text-center mb-2">
                        Cortisol
                      </div>
                      <svg className="h-5 w-5 text-red-500" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5L12 19" stroke="currentColor" strokeWidth="2"/>
                        <path d="M19 12L12 19L5 12" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                    
                    <div className="w-[45%] flex flex-col items-center">
                      <div className="p-2 rounded-full bg-blue-100 text-xs font-medium text-blue-800 text-center mb-2">
                        Testosterone
                      </div>
                      <svg className="h-5 w-5 text-blue-500" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5L12 19" stroke="currentColor" strokeWidth="2"/>
                        <path d="M19 12L12 19L5 12" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                  </div>
                  
                  {/* Intersection pathways */}
                  <div className="absolute top-[80px] left-0 right-0 px-4">
                    {/* Common pathways box */}
                    <div className="mx-auto w-3/4 bg-purple-50 p-2 rounded-md border border-purple-200">
                      <h5 className="text-xs font-medium text-purple-800 text-center mb-1">Shared Signaling Pathways</h5>
                      <ul className="text-[10px] space-y-1">
                        <li className="flex items-center justify-between">
                          <span>IKK/NF-κB</span>
                          <div className="flex items-center space-x-1">
                            <span className="text-red-600">↓</span>
                            <span className="text-blue-600">↓</span>
                          </div>
                        </li>
                        <li className="flex items-center justify-between">
                          <span>mTOR Pathway</span>
                          <div className="flex items-center space-x-1">
                            <span className="text-red-600">↓</span>
                            <span className="text-blue-600">↑</span>
                          </div>
                        </li>
                        <li className="flex items-center justify-between">
                          <span>FOXO Transcription Factors</span>
                          <div className="flex items-center space-x-1">
                            <span className="text-red-600">↑</span>
                            <span className="text-blue-600">↓</span>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* Downstream effects */}
                  <div className="absolute bottom-0 left-0 right-0 flex justify-center">
                    <div className="w-5/6 bg-gray-50 p-3 rounded-md border border-gray-200">
                      <h5 className="text-xs font-medium text-gray-700 mb-2 text-center">Physiological Outcomes</h5>
                      
                      <div className="grid grid-cols-2 gap-3">
                        <div className="bg-red-50 p-2 rounded border border-red-100">
                          <h6 className="text-[10px] font-medium text-red-700 mb-1">Cortisol Dominance</h6>
                          <ul className="text-[9px] list-disc list-inside text-red-600 space-y-0.5">
                            <li>↑ Muscle protein catabolism</li>
                            <li>↑ Abdominal fat accumulation</li>
                            <li>↑ Insulin resistance</li>
                            <li>↓ Immune function</li>
                          </ul>
                        </div>
                        
                        <div className="bg-blue-50 p-2 rounded border border-blue-100">
                          <h6 className="text-[10px] font-medium text-blue-700 mb-1">Testosterone Dominance</h6>
                          <ul className="text-[9px] list-disc list-inside text-blue-600 space-y-0.5">
                            <li>↑ Muscle protein synthesis</li>
                            <li>↓ Body fat percentage</li>
                            <li>↑ Insulin sensitivity</li>
                            <li>↑ Bone mineral density</li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="text-xs text-gray-600">
                <strong>Clinical implications:</strong> The effects of one hormone can be context-dependent on the level of the other. In patients with low testosterone, the same level of cortisol might have a more pronounced catabolic effect than in eugonadal individuals.
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 8,
      title: "Immune System Cross-Talk",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">Immunomodulatory Effects</h3>
              
              <div className="bg-white p-3 rounded-lg shadow-md mb-4">
                <table className="w-full text-sm">
                  <thead>
                    <tr>
                      <th className="border-b-2 border-gray-200 text-left pb-2 w-1/3">Parameter</th>
                      <th className="border-b-2 border-gray-200 text-center pb-2">Cortisol</th>
                      <th className="border-b-2 border-gray-200 text-center pb-2">Testosterone</th>
                    </tr>
                  </thead>
                  <tbody className="text-xs">
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100 font-medium">Inflammatory Response</td>
                      <td className="py-2 border-b border-gray-100 text-center text-red-600">
                        <span className="font-bold">↓↓↓</span><br/>
                        <span className="text-[9px]">(Strongly suppresses)</span>
                      </td>
                      <td className="py-2 border-b border-gray-100 text-center text-blue-600">
                        <span className="font-bold">↓</span><br/>
                        <span className="text-[9px]">(Mildly suppresses)</span>
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100 font-medium">Pro-inflammatory Cytokines<br/>(IL-1β, IL-6, TNF-α)</td>
                      <td className="py-2 border-b border-gray-100 text-center text-red-600 font-medium">↓↓</td>
                      <td className="py-2 border-b border-gray-100 text-center text-blue-600 font-medium">↓</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100 font-medium">Anti-inflammatory Factors<br/>(IL-10)</td>
                      <td className="py-2 border-b border-gray-100 text-center text-green-600 font-medium">↑</td>
                      <td className="py-2 border-b border-gray-100 text-center text-green-600 font-medium">↑</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100 font-medium">T Cell Function</td>
                      <td className="py-2 border-b border-gray-100 text-center text-red-600 font-medium">↓↓</td>
                      <td className="py-2 border-b border-gray-100 text-center text-blue-600 font-medium">↓</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 font-medium">Overall Immune Effect</td>
                      <td className="py-2 text-center text-gray-700">
                        <span className="font-medium">Immunosuppressive</span><br/>
                        <span className="text-[9px]">(Broad-spectrum)</span>
                      </td>
                      <td className="py-2 text-center text-gray-700">
                        <span className="font-medium">Immune-calibrating</span><br/>
                        <span className="text-[9px]">(More selective)</span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="bg-amber-50 p-3 rounded-md border border-amber-200">
                <h4 className="text-amber-800 font-medium text-sm mb-1">Clinical Observations</h4>
                <ul className="text-xs space-y-1 list-disc list-inside">
                  <li>Men have lower rates of many autoimmune diseases compared to women</li>
                  <li>Hypogonadal men often have elevated inflammatory markers (IL-6, TNF-α, CRP)</li>
                  <li>Testosterone therapy in hypogonadal men can reduce inflammatory markers</li>
                  <li>Chronic high cortisol impairs wound healing and increases infection risk</li>
                </ul>
              </div>
            </div>
            
            <div>
              <div className="bg-white p-4 rounded-lg shadow-md">
                <h4 className="text-sm font-medium text-center text-gray-700 mb-3">Inflammatory Balance Regulation</h4>
                
                <div className="relative h-[280px]">
                  {/* Central immune cell */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 h-20 w-20 rounded-full bg-blue-50 border-2 border-blue-200 flex items-center justify-center z-10">
                    <div className="text-xs text-center">
                      <div className="font-medium">Immune Cell</div>
                      <div className="text-[9px] mt-0.5">(e.g., macrophage)</div>
                    </div>
                  </div>
                  
                  {/* Cortisol influence */}
                  <div className="absolute top-0 left-0 w-2/5">
                    <div className="bg-red-100 p-2 rounded text-xs font-medium text-center w-full">
                      Cortisol
                    </div>
                    <div className="flex justify-center my-2">
                      <svg className="h-8 w-5 text-red-500" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5L12 19" stroke="currentColor" strokeWidth="2"/>
                        <path d="M19 12L12 19L5 12" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                    <div className="bg-white p-2 rounded-md border border-gray-200 text-[9px]">
                      <ul className="list-disc list-inside space-y-0.5">
                        <li>Inhibits NF-κB (↓ inflammation)</li>
                        <li>Promotes IκB (↓ inflammation)</li>
                        <li>Suppresses cyclooxygenase-2</li>
                        <li>Reduces leukocyte migration</li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* Testosterone influence */}
                  <div className="absolute top-0 right-0 w-2/5">
                    <div className="bg-blue-100 p-2 rounded text-xs font-medium text-center w-full">
                      Testosterone
                    </div>
                    <div className="flex justify-center my-2">
                      <svg className="h-8 w-5 text-blue-500" viewBox="0 0 24 24" fill="none">
                        <path d="M12 5L12 19" stroke="currentColor" strokeWidth="2"/>
                        <path d="M19 12L12 19L5 12" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    </div>
                    <div className="bg-white p-2 rounded-md border border-gray-200 text-[9px]">
                      <ul className="list-disc list-inside space-y-0.5">
                        <li>Promotes IL-10 (anti-inflammatory)</li>
                        <li>Reduces TNF-α production</li>
                        <li>Modulates T-cell differentiation</li>
                        <li>↓ Th1 activity in some contexts</li>
                      </ul>
                    </div>
                  </div>
                  
                  {/* Bottom output - inflammatory mediators */}
                  <div className="absolute bottom-0 left-0 right-0 flex justify-center">
                    <div className="w-4/5 grid grid-cols-2 gap-2">
                      <div className="bg-red-50 p-2 rounded text-[10px] text-center">
                        <div className="font-medium text-red-700 mb-1">Pro-inflammatory<br />Cytokines</div>
                        <div className="flex justify-center space-x-2">
                          <div className="bg-white px-1 py-0.5 rounded text-[9px] text-red-600">IL-1β</div>
                          <div className="bg-white px-1 py-0.5 rounded text-[9px] text-red-600">IL-6</div>
                          <div className="bg-white px-1 py-0.5 rounded text-[9px] text-red-600">TNF-α</div>
                        </div>
                      </div>
                      
                      <div className="bg-green-50 p-2 rounded text-[10px] text-center">
                        <div className="font-medium text-green-700 mb-1">Anti-inflammatory<br />Mediators</div>
                        <div className="flex justify-center space-x-2">
                          <div className="bg-white px-1 py-0.5 rounded text-[9px] text-green-600">IL-10</div>
                          <div className="bg-white px-1 py-0.5 rounded text-[9px] text-green-600">TGF-β</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="text-xs mt-3 text-gray-600">
                <strong>Optimal balance:</strong> Adequate testosterone levels with normal cortisol rhythms help calibrate immune responses—strong enough to fight infection but controlled enough to prevent excessive inflammation and autoimmunity.
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 9,
      title: "Neurological Effects and Stress Resilience",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">Behavioral and Cognitive Impacts</h3>
              
              <div className="mb-4 bg-gray-50 p-3 rounded-md relative">
                <div className="text-center mb-3">
                  <span className="text-sm font-medium">Simplified Behavioral Response Model</span>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-red-50 p-3 rounded-md border border-red-200">
                    <h4 className="text-xs font-medium text-red-700 mb-2 text-center">Cortisol</h4>
                    <ul className="text-[10px] text-gray-700 space-y-1 list-disc list-inside">
                      <li>Promotes "flight" responses</li>
                      <li>Enhances fear conditioning</li>
                      <li>Increases vigilance and caution</li>
                      <li>Facilitates memory of threats</li>
                      <li>Promotes withdrawal behavior</li>
                    </ul>
                  </div>
                  
                  <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
                    <h4 className="text-xs font-medium text-blue-700 mb-2 text-center">Testosterone</h4>
                    <ul className="text-[10px] text-gray-700 space-y-1 list-disc list-inside">
                      <li>Promotes "fight" responses</li>
                      <li>Reduces fear sensitivity</li>
                      <li>Increases confidence and risk-taking</li>
                      <li>Enhances reward sensitivity</li>
                      <li>Promotes approach behavior</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-3 mx-auto w-3/4 h-px bg-gray-300"></div>
                
                <div className="mt-3 mx-auto w-4/5 bg-white p-2 rounded-md border border-gray-200 text-center">
                  <p className="text-[10px] text-gray-700">
                    <strong>Optimal survival strategy</strong> balances both response types:<br />
                    Vigilance and caution with courage and decisive action
                  </p>
                </div>
              </div>
              
              <div className="bg-white p-3 rounded-lg shadow-md">
                <h4 className="text-sm font-medium text-gray-800 mb-2">Cognitive Domain Effects</h4>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-1 font-medium text-gray-700">Domain</th>
                        <th className="text-center py-1 font-medium text-gray-700">Chronic High Cortisol</th>
                        <th className="text-center py-1 font-medium text-gray-700">Low Testosterone</th>
                      </tr>
                    </thead>
                    <tbody className="text-[10px]">
                      <tr className="border-b border-gray-100">
                        <td className="py-1 pr-2 font-medium">Memory</td>
                        <td className="py-1 text-center text-red-600">↓ Especially declarative</td>
                        <td className="py-1 text-center text-red-600">↓ Mild impairment</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-1 pr-2 font-medium">Executive Function</td>
                        <td className="py-1 text-center text-red-600">↓ Impaired</td>
                        <td className="py-1 text-center text-red-600">↓ Reduced</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-1 pr-2 font-medium">Decision Making</td>
                        <td className="py-1 text-center text-orange-600">Risk-averse</td>
                        <td className="py-1 text-center text-gray-600">Indecisive</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-1 pr-2 font-medium">Motivation</td>
                        <td className="py-1 text-center text-red-600">↓ Reduced</td>
                        <td className="py-1 text-center text-red-600">↓↓ Significantly reduced</td>
                      </tr>
                      <tr>
                        <td className="py-1 pr-2 font-medium">Mood</td>
                        <td className="py-1 text-center text-red-600">Anxiety, irritability</td>
                        <td className="py-1 text-center text-red-600">Depression, fatigue</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            <div>
              <div className="bg-white p-4 rounded-lg shadow-md mb-4">
                <h4 className="text-sm font-medium text-blue-900 mb-3">Brain Regions and Interactions</h4>
                
                <div className="relative h-[320px]">
                  {/* Brain outline */}
                  <div className="absolute top-0 inset-x-0 flex items-center justify-center h-40">
                    <div className="w-56 h-40 bg-gray-100 rounded-[100px] border border-gray-300 relative">
                      {/* Prefrontal cortex */}
                      <div className="absolute top-2 left-3 w-14 h-10 bg-blue-100 rounded-md border border-blue-200">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-[8px] text-center">
                            <strong className="text-blue-800">PFC</strong>
                          </div>
                        </div>
                      </div>
                      
                      {/* Hippocampus */}
                      <div className="absolute top-[45%] left-[40%] w-8 h-6 bg-green-100 rounded-md border border-green-200">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-[8px] text-center">
                            <strong className="text-green-800">HC</strong>
                          </div>
                        </div>
                      </div>
                      
                      {/* Amygdala */}
                      <div className="absolute top-[60%] left-[35%] w-7 h-5 bg-red-100 rounded-md border border-red-200">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-[8px] text-center">
                            <strong className="text-red-800">AMY</strong>
                          </div>
                        </div>
                      </div>
                      
                      {/* Hypothalamus */}
                      <div className="absolute top-[70%] left-[45%] w-7 h-5 bg-purple-100 rounded-md border border-purple-200">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-[8px] text-center">
                            <strong className="text-purple-800">HYP</strong>
                          </div>
                        </div>
                      </div>
                      
                      {/* Striatum */}
                      <div className="absolute top-[45%] right-[25%] w-8 h-6 bg-amber-100 rounded-md border border-amber-200">
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="text-[8px] text-center">
                            <strong className="text-amber-800">STR</strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Hormone effects */}
                  <div className="absolute bottom-0 left-0 right-0 grid grid-cols-2 gap-2 text-[9px]">
                    <div className="bg-red-50 p-2 rounded border border-red-100">
                      <div className="font-medium text-red-700 mb-1">Cortisol Effects</div>
                      <ul className="list-disc list-inside space-y-0.5 text-gray-700">
                        <li>↑ Amygdala reactivity</li>
                        <li>↓ PFC executive control</li>
                        <li>Hippocampal atrophy (chronic)</li>
                        <li>Impairs neurogenesis</li>
                      </ul>
                    </div>
                    
                    <div className="bg-blue-50 p-2 rounded border border-blue-100">
                      <div className="font-medium text-blue-700 mb-1">Testosterone Effects</div>
                      <ul className="list-disc list-inside space-y-0.5 text-gray-700">
                        <li>↓ Amygdala reactivity to threat</li>
                        <li>↑ Striatal activation (reward)</li>
                        <li>Neuroprotective effects</li>
                        <li>May enhance neuroplasticity</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-amber-50 p-3 rounded-md border border-amber-200">
                <h4 className="text-amber-800 font-medium text-sm mb-1">Clinical Applications</h4>
                <p className="text-xs text-gray-700 mb-2">
                  Hormonal balance plays a crucial role in stress resilience, cognitive health, and emotional well-being:
                </p>
                <ul className="text-xs space-y-1 list-disc list-inside text-gray-700">
                  <li>Depression with low testosterone may benefit from hormone optimization alongside antidepressants</li>
                  <li>Stress management techniques that reduce pathological cortisol can improve cognitive function</li>
                  <li>PTSD treatment may be enhanced by addressing hormonal balance</li>
                  <li>Cognitive decline in aging is associated with both cortisol elevations and testosterone reductions</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 10,
      title: "Traditional Biomarkers of Cortisol",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">Clinical Assessment Methods</h3>
              
              <div className="bg-white p-4 rounded-lg shadow-md mb-4">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className="border-b-2 border-gray-200 text-left pb-2 font-medium">Biomarker</th>
                      <th className="border-b-2 border-gray-200 text-left pb-2 font-medium">Sample Type</th>
                      <th className="border-b-2 border-gray-200 text-left pb-2 font-medium">Clinical Utility</th>
                    </tr>
                  </thead>
                  <tbody className="text-xs">
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100 font-medium">Serum Cortisol</td>
                      <td className="py-2 pr-2 border-b border-gray-100">Blood</td>
                      <td className="py-2 border-b border-gray-100">Morning peak (8 AM): 10-20 μg/dL<br/>Low: adrenal insufficiency<br/>High: Cushing's syndrome</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100 font-medium">Salivary Cortisol</td>
                      <td className="py-2 pr-2 border-b border-gray-100">Saliva</td>
                      <td className="py-2 border-b border-gray-100">Free cortisol assessment<br/>Late-night sample for Cushing's<br/>Diurnal profiling</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100 font-medium">24-hour Urinary Free Cortisol</td>
                      <td className="py-2 pr-2 border-b border-gray-100">Urine</td>
                      <td className="py-2 border-b border-gray-100">Aggregate 24-hour cortisol output<br/>Elevated in Cushing's</td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100 font-medium">ACTH Level</td>
                      <td className="py-2 pr-2 border-b border-gray-100">Blood</td>
                      <td className="py-2 border-b border-gray-100">Differential diagnosis:<br/>High ACTH + low cortisol = primary adrenal failure<br/>Low ACTH + low cortisol = secondary (pituitary)</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="bg-amber-50 p-3 rounded-md border border-amber-200">
                <h4 className="text-sm font-medium text-amber-800 mb-2">Standard Dynamic Tests</h4>
                <ul className="text-xs space-y-2 list-disc list-inside text-gray-700">
                  <li>
                    <span className="font-medium text-amber-700">ACTH Stimulation Test:</span> Measures adrenal response to synthetic ACTH (cosyntropin). Normal response: peak cortisol &gt;18 μg/dL within 60 minutes.
                  </li>
                  <li>
                    <span className="font-medium text-amber-700">Dexamethasone Suppression:</span> Overnight test (1mg dex) should suppress 8 AM cortisol to &lt;5 μg/dL. Failure suggests Cushing's syndrome.
                  </li>
                </ul>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">Hormone Interactions</h3>
              
              <div className="bg-white p-4 rounded-lg shadow-md mb-4">
                <h4 className="text-sm font-medium text-center text-gray-700 mb-4">Cortisol Assessment Context</h4>
                
                <div className="relative h-[280px]">
                  {/* Central circle - Cortisol */}
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                    <div className="h-20 w-20 rounded-full bg-red-100 border-2 border-red-300 flex items-center justify-center">
                      <div className="text-sm font-bold text-red-700 text-center">
                        Cortisol<br/>Assessment
                      </div>
                    </div>
                  </div>
                  
                  {/* Connected factors */}
                  <div className="absolute top-[15px] left-1/2 transform -translate-x-1/2">
                    <div className="flex flex-col items-center">
                      <div className="h-14 w-14 rounded-full bg-blue-100 border border-blue-300 flex items-center justify-center">
                        <span className="text-xs font-medium text-blue-700 text-center">Circadian<br/>Timing</span>
                      </div>
                      <div className="h-10 border-l border-gray-300"></div>
                    </div>
                  </div>
                  
                  <div className="absolute top-[60px] right-[20px]">
                    <div className="flex items-center">
                      <div className="h-14 w-14 rounded-full bg-green-100 border border-green-300 flex items-center justify-center">
                        <span className="text-xs font-medium text-green-700 text-center">CBG<br/>Levels</span>
                      </div>
                      <div className="w-16 border-t border-gray-300 -ml-2"></div>
                    </div>
                  </div>
                  
                  <div className="absolute bottom-[60px] right-[20px]">
                    <div className="flex items-center">
                      <div className="h-14 w-14 rounded-full bg-purple-100 border border-purple-300 flex items-center justify-center">
                        <span className="text-xs font-medium text-purple-700 text-center">ACTH<br/>Status</span>
                      </div>
                      <div className="w-16 border-t border-gray-300 -ml-2"></div>
                    </div>
                  </div>
                  
                  <div className="absolute bottom-[15px] left-1/2 transform -translate-x-1/2">
                    <div className="flex flex-col items-center">
                      <div className="h-10 border-l border-gray-300"></div>
                      <div className="h-14 w-14 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center">
                        <span className="text-xs font-medium text-amber-700 text-center">Adrenal<br/>Function</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute bottom-[60px] left-[20px]">
                    <div className="flex items-center">
                      <div className="w-16 border-t border-gray-300 -mr-2"></div>
                      <div className="h-14 w-14 rounded-full bg-indigo-100 border border-indigo-300 flex items-center justify-center">
                        <span className="text-xs font-medium text-indigo-700 text-center">DHEA-S<br/>Balance</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute top-[60px] left-[20px]">
                    <div className="flex items-center">
                      <div className="w-16 border-t border-gray-300 -mr-2"></div>
                      <div className="h-14 w-14 rounded-full bg-cyan-100 border border-cyan-300 flex items-center justify-center">
                        <span className="text-xs font-medium text-cyan-700 text-center">Testosterone<br/>Ratio</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-3 rounded-lg shadow-md">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Clinical Considerations</h4>
                <ul className="text-xs list-disc list-inside space-y-1 text-gray-700">
                  <li>Single timepoint measures may miss important patterns</li>
                  <li>Hormone binding proteins affect active cortisol availability</li>
                  <li>Concurrent assessment of related hormones provides context</li>
                  <li>Diurnal rhythm is a critical interpretive factor</li>
                  <li>Stress during sampling can artifactually elevate results</li>
                </ul>
                
                <div className="mt-3 p-2 bg-gray-50 rounded-md text-xs text-gray-600 italic">
                  <span className="font-medium">Note:</span> Interpretation should consider the clinical context, timing, and interacting factors rather than isolated values alone.
                </div>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 11,
      title: "Emerging & Novel Biomarkers",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">Advanced Assessment Technologies</h3>
              
              <div className="bg-white p-4 rounded-lg shadow-md mb-4">
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-800 mb-2">Cortisol Metabolomics (Steroid Profiling)</h4>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <div className="flex items-start">
                      <div className="mr-3 h-8 w-8 flex-shrink-0 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-lg text-blue-700">🧪</span>
                      </div>
                      <div className="text-xs text-gray-700">
                        <p className="mb-1">
                          LC-MS/MS technology enables comprehensive profiling of multiple cortisol metabolites simultaneously, providing insight into enzymatic activity and tissue-specific cortisol metabolism.
                        </p>
                        <p className="text-[10px] text-gray-500 italic">
                          Applications: Detection of subtle cortisol excess, enzyme deficiencies, personalized cortisol profiling
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-800 mb-2">Hair Cortisol Analysis</h4>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <div className="flex items-start">
                      <div className="mr-3 h-8 w-8 flex-shrink-0 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-lg text-green-700">📏</span>
                      </div>
                      <div className="text-xs text-gray-700">
                        <p className="mb-1">
                          Cortisol accumulates in hair, providing a retrospective timeline of cortisol exposure over months (1cm ≈ 1 month). Acts as an "HbA1c-like" marker for long-term cortisol status.
                        </p>
                        <p className="text-[10px] text-gray-500 italic">
                          Applications: Chronic stress assessment, cyclical Cushing's diagnosis, monitoring treatment responses
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-800 mb-2">Salivary Cortisol Awakening Response</h4>
                  <div className="bg-gray-50 p-3 rounded-md">
                    <div className="flex items-start">
                      <div className="mr-3 h-8 w-8 flex-shrink-0 bg-amber-100 rounded-full flex items-center justify-center">
                        <span className="text-lg text-amber-700">⏰</span>
                      </div>
                      <div className="text-xs text-gray-700">
                        <p className="mb-1">
                          Multiple-timepoint assessment of the morning cortisol surge upon awakening. Normally shows 50-75% increase in first 30-45 minutes after waking.
                        </p>
                        <p className="text-[10px] text-gray-500 italic">
                          Applications: Stress resilience assessment, burnout evaluation, chronic fatigue investigation
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-indigo-50 p-3 rounded-md border border-indigo-200">
                <h4 className="text-sm font-medium text-indigo-800 mb-2">In Development</h4>
                <div className="text-xs space-y-3">
                  <div className="flex items-start">
                    <div className="mr-2 h-4 w-4 mt-0.5 rounded-full bg-indigo-100 border border-indigo-300 flex items-center justify-center">
                      <span className="text-[10px] font-medium text-indigo-700">1</span>
                    </div>
                    <div>
                      <p className="font-medium text-indigo-700">Point-of-Care & Wearables</p>
                      <p className="text-gray-700">Transdermal patches for continuous cortisol monitoring in sweat, and immunochromatographic assays for rapid cortisol assessment</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start">
                    <div className="mr-2 h-4 w-4 mt-0.5 rounded-full bg-indigo-100 border border-indigo-300 flex items-center justify-center">
                      <span className="text-[10px] font-medium text-indigo-700">2</span>
                    </div>
                    <div>
                      <p className="font-medium text-indigo-700">AI-Driven Hormone Profiling</p>
                      <p className="text-gray-700">Machine learning algorithms to detect significant patterns in cortisol data that might predict clinical outcomes or guide interventions</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">Integrated Biomarkers</h3>
              
              <div className="bg-white p-4 rounded-lg shadow-md mb-4">
                <h4 className="text-sm font-medium text-gray-800 mb-3">Hormone Ratios & Composite Markers</h4>
                
                <div className="grid grid-cols-1 gap-4">
                  {/* T/C Ratio */}
                  <div className="bg-blue-50 p-3 rounded-md border border-blue-100">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-sm font-medium text-blue-800">Testosterone:Cortisol Ratio</h5>
                      <div className="px-2 py-1 bg-blue-100 rounded text-xs font-medium text-blue-700">
                        Anabolic/Catabolic Balance
                      </div>
                    </div>
                    <div className="mb-2">
                      <div className="bg-white rounded-md p-2 text-center">
                        <div className="text-sm flex items-center justify-center">
                          <span className="text-blue-700 font-medium">Free Testosterone</span>
                          <span className="mx-2 text-gray-500">/</span>
                          <span className="text-red-700 font-medium">Cortisol</span>
                        </div>
                      </div>
                    </div>
                    <ul className="text-xs space-y-1 list-disc list-inside text-gray-700">
                      <li>Used to monitor overtraining in athletes (drop of &gt;30% indicates concern)</li>
                      <li>Low ratio associated with catabolism, poor recovery, reduced performance</li>
                      <li>Applications in exercise science, sports medicine, and chronic stress</li>
                    </ul>
                  </div>
                  
                  {/* DHEA/Cortisol Ratio */}
                  <div className="bg-purple-50 p-3 rounded-md border border-purple-100">
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="text-sm font-medium text-purple-800">Cortisol:DHEA Ratio</h5>
                      <div className="px-2 py-1 bg-purple-100 rounded text-xs font-medium text-purple-700">
                        Stress & Aging Marker
                      </div>
                    </div>
                    <div className="mb-2">
                      <div className="bg-white rounded-md p-2 text-center">
                        <div className="text-sm flex items-center justify-center">
                          <span className="text-red-700 font-medium">Cortisol</span>
                          <span className="mx-2 text-gray-500">/</span>
                          <span className="text-purple-700 font-medium">DHEA(-S)</span>
                        </div>
                      </div>
                    </div>
                    <ul className="text-xs space-y-1 list-disc list-inside text-gray-700">
                      <li>DHEA often counterbalances cortisol's immunological effects</li>
                      <li>Ratio typically increases with age (rising cortisol, falling DHEA)</li>
                      <li>High ratio linked to cognitive decline and adverse health outcomes</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-md">
                <h4 className="text-sm font-medium text-gray-800 mb-3">Genetic & Epigenetic Markers</h4>
                
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className="border-b-2 border-gray-200 text-left pb-2 font-medium">Marker Type</th>
                      <th className="border-b-2 border-gray-200 text-left pb-2 font-medium">Examples</th>
                      <th className="border-b-2 border-gray-200 text-left pb-2 font-medium">Significance</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100 font-medium">GR Polymorphisms</td>
                      <td className="py-2 pr-2 border-b border-gray-100">
                        ER22/23EK<br/>
                        BclI, N363S
                      </td>
                      <td className="py-2 border-b border-gray-100">
                        Affects cortisol sensitivity<br/>
                        Influences metabolic effects
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100 font-medium">11β-HSD Variants</td>
                      <td className="py-2 pr-2 border-b border-gray-100">
                        HSD11B1, HSD11B2<br/>
                        gene variants
                      </td>
                      <td className="py-2 border-b border-gray-100">
                        Alters cortisol-cortisone conversion<br/>
                        Tissue-specific cortisol activity
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100 font-medium">Epigenetic Markers</td>
                      <td className="py-2 pr-2 border-b border-gray-100">
                        NR3C1 methylation<br/>
                        FKBP5 methylation
                      </td>
                      <td className="py-2 border-b border-gray-100">
                        Affected by early life stress<br/>
                        Alters stress responsiveness
                      </td>
                    </tr>
                  </tbody>
                </table>
                
                <div className="mt-3 bg-amber-50 p-2 rounded-md text-xs text-amber-800">
                  <div className="font-medium mb-1">Clinical Relevance:</div>
                  <p>These genetic and epigenetic variations help explain why individuals with similar cortisol levels may experience dramatically different physiological effects and health outcomes. They represent an emerging frontier in personalized cortisol assessment.</p>
                </div>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 12,
      title: "Cortisol in Longevity Medicine",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">T/C Ratio as a Health Marker</h3>
              
              <div className="bg-white p-4 rounded-lg shadow-md mb-4">
                <h4 className="text-sm font-medium text-center text-gray-800 mb-3">Anabolic-Catabolic Balance Assessment</h4>
                
                <div className="relative h-[240px]">
                  {/* Balance scale visualization */}
                  <div className="absolute top-4 left-1/2 transform -translate-x-1/2 h-6 w-1 bg-gray-400"></div>
                  <div className="absolute top-0 left-1/2 transform -translate-x-1/2 h-4 w-20 bg-gray-400 rounded"></div>
                  
                  {/* Left scale - Anabolic */}
                  <div className="absolute top-10 left-[30%] flex flex-col items-center">
                    <div className="h-24 w-1 bg-gray-400"></div>
                    <div className="mt-2 h-20 w-20 rounded-full bg-blue-100 border-2 border-blue-300 flex items-center justify-center z-10">
                      <div className="text-sm font-bold text-blue-700 text-center">
                        Testosterone<br/>
                        <span className="text-xs font-normal">(Anabolic)</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Right scale - Catabolic */}
                  <div className="absolute top-10 right-[30%] flex flex-col items-center">
                    <div className="h-40 w-1 bg-gray-400"></div>
                    <div className="mt-2 h-20 w-20 rounded-full bg-red-100 border-2 border-red-300 flex items-center justify-center z-10">
                      <div className="text-sm font-bold text-red-700 text-center">
                        Cortisol<br/>
                        <span className="text-xs font-normal">(Catabolic)</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Indicators */}
                  <div className="absolute bottom-2 left-0 right-0 flex justify-between px-8">
                    <div className="text-center">
                      <div className="mb-1 w-full h-1 bg-gradient-to-r from-blue-500 to-blue-200 rounded"></div>
                      <div className="text-xs font-medium text-blue-700">Optimal T/C Ratio</div>
                      <div className="text-[10px] text-blue-600">Recovery, growth, adaptation</div>
                    </div>
                    
                    <div className="text-center">
                      <div className="mb-1 w-full h-1 bg-gradient-to-r from-amber-200 to-red-500 rounded"></div>
                      <div className="text-xs font-medium text-red-700">Low T/C Ratio</div>
                      <div className="text-[10px] text-red-600">Overtraining, stress, illness</div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-2 bg-gray-50 p-2 rounded text-xs">
                  <p className="font-medium text-gray-700 mb-1">Clinical Applications:</p>
                  <ul className="list-disc list-inside text-gray-600 space-y-1">
                    <li>Sports medicine: Monitoring training load and recovery status</li>
                    <li>Metabolic health: Low T/C associated with insulin resistance</li>
                    <li>Aging: Progressive decline in ratio correlates with sarcopenia</li>
                    <li>Stress disorders: Tracking intervention effectiveness</li>
                  </ul>
                </div>
              </div>
              
              <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Reference Ranges & Interpretation</h4>
                <div className="bg-white p-2 rounded mb-2">
                  <div className="text-xs text-center mb-1 font-medium text-gray-700">Typical Free T/C Ratio Ranges</div>
                  <div className="flex justify-between text-[10px] text-gray-600">
                    <div>Very Low</div>
                    <div>Low</div>
                    <div>Normal</div>
                    <div>Optimal</div>
                  </div>
                  <div className="w-full h-2 mt-1 bg-gradient-to-r from-red-500 via-amber-300 to-green-500 rounded-full"></div>
                </div>
                <ul className="text-xs space-y-1.5 text-gray-700">
                  <li className="flex items-start">
                    <div className="h-4 w-4 mt-0.5 rounded-full bg-red-100 flex-shrink-0 flex items-center justify-center mr-2">
                      <span className="text-[8px] font-bold text-red-600">!</span>
                    </div>
                    <span>
                      Drop of &gt;30% from individual baseline may indicate overtraining/stress
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="h-4 w-4 mt-0.5 rounded-full bg-amber-100 flex-shrink-0 flex items-center justify-center mr-2">
                      <span className="text-[8px] font-bold text-amber-600">i</span>
                    </div>
                    <span>
                      Context matters: age, gender, training status affect reference ranges
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="h-4 w-4 mt-0.5 rounded-full bg-green-100 flex-shrink-0 flex items-center justify-center mr-2">
                      <span className="text-[8px] font-bold text-green-600">✓</span>
                    </div>
                    <span>
                      Serial measurements more valuable than single timepoint assessments
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">Findings in Longevity-Focused Individuals</h3>
              
              <div className="bg-white p-4 rounded-lg shadow-md mb-4">
                <h4 className="text-sm font-medium text-center text-gray-800 mb-3">Enhanced Stress Resilience</h4>
                
                <div className="relative overflow-hidden rounded-lg bg-gradient-to-b from-blue-50 to-indigo-50 p-4 h-[180px]">
                  {/* Family tree representation */}
                  <div className="absolute top-4 left-1/2 transform -translate-x-1/2 flex flex-col items-center">
                    <div className="h-8 w-8 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center">
                      <span className="text-[10px] font-medium text-amber-800">90+</span>
                    </div>
                    <div className="h-4 w-px bg-gray-300"></div>
                    <div className="flex items-center">
                      <div className="h-8 w-8 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center">
                        <span className="text-[10px] font-medium text-amber-800">90+</span>
                      </div>
                      <div className="w-16 h-px bg-gray-300"></div>
                      <div className="h-8 w-8 rounded-full bg-amber-100 border border-amber-300 flex items-center justify-center">
                        <span className="text-[10px] font-medium text-amber-800">95+</span>
                      </div>
                    </div>
                    <div className="h-4 w-px bg-gray-300"></div>
                    <div className="h-8 w-8 rounded-full bg-blue-100 border border-blue-300 flex items-center justify-center">
                      <span className="text-[10px] font-medium text-blue-800">65</span>
                    </div>
                    <div className="absolute left-1/2 transform -translate-x-1/2 -bottom-2">
                      <div className="text-xs text-center font-medium text-blue-800">
                        Longevity Family
                      </div>
                    </div>
                  </div>
                  
                  {/* Cortisol resistance concept */}
                  <div className="absolute bottom-4 left-4">
                    <div className="p-2 bg-white rounded-md shadow-sm w-[120px]">
                      <div className="text-[10px] font-medium text-gray-700 mb-1 text-center">
                        Similar Cortisol Level
                      </div>
                      <div className="h-1 w-full bg-red-200 rounded-full"></div>
                      <div className="mt-2 text-[8px] text-center text-gray-600">
                        But reduced tissue damage
                      </div>
                    </div>
                  </div>
                  
                  <div className="absolute bottom-4 right-4">
                    <div className="p-2 bg-white rounded-md shadow-sm w-[120px]">
                      <div className="text-[10px] font-medium text-gray-700 mb-1 text-center">
                        Enhanced Resilience
                      </div>
                      <div className="flex justify-between">
                        <div className="h-3 w-3 rounded-full bg-green-100 border border-green-300"></div>
                        <div className="h-3 w-3 rounded-full bg-green-100 border border-green-300"></div>
                        <div className="h-3 w-3 rounded-full bg-green-100 border border-green-300"></div>
                      </div>
                      <div className="mt-2 text-[8px] text-center text-gray-600">
                        Tissue protection mechanisms
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-3 text-xs">
                  <p className="mb-2 text-gray-700">
                    Research from the Leiden Longevity Study revealed that offspring of long-lived families demonstrate enhanced stress resilience. Despite similar cortisol levels to controls, they show reduced negative effects from cortisol exposure.
                  </p>
                  <div className="bg-amber-50 p-2 rounded text-amber-800">
                    <span className="font-medium">Key Finding:</span> Tissue responses to cortisol, not just absolute levels, appear to be a crucial factor in longevity and healthy aging. This suggests cellular resilience mechanisms that protect against stress-induced damage.
                  </div>
                </div>
              </div>
              
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 p-4 rounded-lg border border-blue-100">
                <h4 className="text-sm font-medium text-blue-800 mb-3">Caloric Restriction & Stress Hormones</h4>
                
                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div className="bg-white p-2 rounded shadow-sm">
                    <div className="text-xs font-medium text-center text-gray-700 mb-1">Standard Diet</div>
                    <div className="flex items-center justify-between text-[10px] px-1">
                      <div className="flex flex-col items-center">
                        <div className="h-3 w-12 bg-red-200 rounded-t-sm"></div>
                        <span className="text-red-700 mt-1">Cortisol</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="h-6 w-12 bg-purple-200 rounded-t-sm"></div>
                        <span className="text-purple-700 mt-1">DHEA</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="h-4 w-12 bg-blue-200 rounded-t-sm"></div>
                        <span className="text-blue-700 mt-1">T</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-white p-2 rounded shadow-sm">
                    <div className="text-xs font-medium text-center text-gray-700 mb-1">Caloric Restriction</div>
                    <div className="flex items-center justify-between text-[10px] px-1">
                      <div className="flex flex-col items-center">
                        <div className="h-5 w-12 bg-red-200 rounded-t-sm"></div>
                        <span className="text-red-700 mt-1">Cortisol</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="h-7 w-12 bg-purple-200 rounded-t-sm"></div>
                        <span className="text-purple-700 mt-1">DHEA</span>
                      </div>
                      <div className="flex flex-col items-center">
                        <div className="h-3 w-12 bg-blue-200 rounded-t-sm"></div>
                        <span className="text-blue-700 mt-1">T</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <ul className="text-xs space-y-1.5 text-gray-700">
                  <li className="flex items-start">
                    <div className="h-4 w-4 mt-0.5 rounded-full bg-blue-100 flex-shrink-0 flex items-center justify-center mr-2">
                      <span className="text-[8px] font-bold text-blue-600">1</span>
                    </div>
                    <span>
                      Moderate caloric restriction can increase morning cortisol while maintaining or improving insulin sensitivity
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="h-4 w-4 mt-0.5 rounded-full bg-blue-100 flex-shrink-0 flex items-center justify-center mr-2">
                      <span className="text-[8px] font-bold text-blue-600">2</span>
                    </div>
                    <span>
                      "Hormetic stress" from intermittent fasting may activate cellular repair and resilience pathways
                    </span>
                  </li>
                  <li className="flex items-start">
                    <div className="h-4 w-4 mt-0.5 rounded-full bg-blue-100 flex-shrink-0 flex items-center justify-center mr-2">
                      <span className="text-[8px] font-bold text-blue-600">3</span>
                    </div>
                    <span>
                      Habitual circadian-aligned eating patterns support optimal cortisol rhythmicity
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
          
          <div className="mt-6 bg-blue-50 p-4 rounded-lg border border-blue-200">
            <h3 className="text-md font-medium text-blue-800 mb-3">Cortisol Assessment: Future Directions</h3>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-white p-3 rounded-md shadow-sm">
                <div className="h-10 w-10 mx-auto mb-2 bg-green-100 rounded-full flex items-center justify-center">
                  <span className="text-lg text-green-700">🔄</span>
                </div>
                <h4 className="text-xs font-medium text-center text-gray-800 mb-1">Continuous Monitoring</h4>
                <p className="text-[10px] text-center text-gray-600">
                  Wearable sensors and patches will enable real-time cortisol tracking, allowing correlation with lifestyle, stress events, and symptoms
                </p>
              </div>
              
              <div className="bg-white p-3 rounded-md shadow-sm">
                <div className="h-10 w-10 mx-auto mb-2 bg-purple-100 rounded-full flex items-center justify-center">
                  <span className="text-lg text-purple-700">🧬</span>
                </div>
                <h4 className="text-xs font-medium text-center text-gray-800 mb-1">Personalized Reference Ranges</h4>
                <p className="text-[10px] text-center text-gray-600">
                  Integration of genetic, epigenetic, and metabolomic data to develop individualized cortisol interpretation models
                </p>
              </div>
              
              <div className="bg-white p-3 rounded-md shadow-sm">
                <div className="h-10 w-10 mx-auto mb-2 bg-blue-100 rounded-full flex items-center justify-center">
                  <span className="text-lg text-blue-700">🔬</span>
                </div>
                <h4 className="text-xs font-medium text-center text-gray-800 mb-1">Tissue-Specific Assessment</h4>
                <p className="text-[10px] text-center text-gray-600">
                  Advanced imaging and biomarkers to evaluate cortisol activity in specific tissues like brain, muscle, and visceral fat
                </p>
              </div>
            </div>
            
            <div className="mt-4 text-xs text-gray-700 bg-gray-50 p-2 rounded-md">
              <p className="italic">
                "The future of cortisol assessment in longevity medicine will move beyond simply measuring hormone levels toward understanding individual response patterns and tissue-specific effects, enabling truly personalized interventions for stress resilience and healthy aging."
              </p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 13,
      title: "Cortisol and Aging",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">Catabolic Pressure on Tissues</h3>
              
              <div className="bg-white p-4 rounded-lg shadow-md mb-4 text-xs text-gray-700">
                <p className="mb-3">
                  Aging is accompanied by endocrine changes often characterized by a tilt toward catabolism. While cortisol levels don't necessarily increase dramatically with age, older individuals can have:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>More prolonged cortisol elevation under stress</li>
                  <li>Slower return to baseline after stressors</li>
                  <li>Lower anabolic counter-hormones (DHEA, testosterone)</li>
                  <li>Changes in diurnal rhythm patterns rather than baseline increases</li>
                </ul>
              </div>
              
              <div className="bg-white p-4 rounded-lg shadow-md text-xs">
                <h4 className="text-sm font-medium text-gray-800 mb-3">Mitochondrial Impact</h4>
                
                <div className="relative h-[200px] mb-3">
                  {/* Mitochondrion illustration */}
                  <div className="absolute w-[90%] h-[120px] left-[5%] top-[40px] bg-green-50 rounded-[50px] border border-green-200"></div>
                  
                  {/* Inner membrane folds */}
                  <div className="absolute w-[75%] h-[90px] left-[12.5%] top-[55px] flex space-x-2">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="h-full w-2 bg-green-100 rounded-full border border-green-200"></div>
                    ))}
                  </div>
                  
                  {/* DNA circle */}
                  <div className="absolute w-10 h-10 left-[40%] top-[80px] bg-blue-100 rounded-full border border-blue-200 flex items-center justify-center">
                    <div className="w-6 h-6 rounded-full border border-blue-300 border-dashed"></div>
                  </div>
                  
                  {/* GR receptor entering mitochondria */}
                  <div className="absolute top-[30px] left-[60%]">
                    <div className="relative">
                      <div className="w-10 h-10 rounded-full bg-red-100 border border-red-300 flex items-center justify-center">
                        <span className="text-[10px] font-medium text-red-700">GR</span>
                      </div>
                      <div className="absolute top-[15px] left-[-20px] w-20 h-4">
                        <svg width="100%" height="100%" viewBox="0 0 80 16">
                          <path d="M0,8 C20,8 20,2 40,8 C60,14 60,8 80,8" stroke="#EF4444" fill="none" strokeDasharray="3,3" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  
                  {/* Labels */}
                  <div className="absolute top-[10px] left-[40%] text-[10px] text-green-700 font-medium">Mitochondrion</div>
                  <div className="absolute bottom-[10px] left-[40%] text-[10px] text-blue-700 font-medium">mtDNA</div>
                </div>
                
                <div className="space-y-2">
                  <p>
                    <span className="font-medium">Direct effects:</span> The glucocorticoid receptor (GR) can localize to mitochondria and influence mitochondrial DNA gene expression.
                  </p>
                  <p>
                    <span className="font-medium">Dual roles:</span> Low physiological levels support mitochondrial function, but high levels induce mitochondrial dysfunction.
                  </p>
                  <p>
                    <span className="font-medium">Aging impact:</span> Chronic cortisol elevation contributes to mitochondrial aging through oxidative stress and damage to respiratory complexes.
                  </p>
                </div>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">Age-Related Tissue Effects</h3>
              
              <div className="bg-white p-4 rounded-lg shadow-md mb-4">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      <th className="py-2 text-left font-medium text-gray-700 border-b">Tissue/System</th>
                      <th className="py-2 text-left font-medium text-gray-700 border-b">Cortisol's Age-Related Effects</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100 font-medium text-blue-700">Muscle</td>
                      <td className="py-2 border-b border-gray-100">
                        Accelerates protein breakdown, antagonizes muscle-building signals, exacerbates sarcopenia in aging, especially with diminished anabolic hormones
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100 font-medium text-blue-700">Brain</td>
                      <td className="py-2 border-b border-gray-100">
                        Higher late-life cortisol correlates with brain atrophy, reduced hippocampal volume, and cognitive decline; older neurons show greater vulnerability to cortisol's effects
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100 font-medium text-blue-700">Metabolism</td>
                      <td className="py-2 border-b border-gray-100">
                        Contributes to visceral fat accumulation and glucose intolerance; postmenopausal women lose estrogen's protective effects, with central fat producing more cortisol locally
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 border-b border-gray-100 font-medium text-blue-700">Telomeres</td>
                      <td className="py-2 border-b border-gray-100">
                        Chronic stress with cortisol elevation linked to shorter telomeres; chronically stressed individuals show cellular aging comparable to those a decade older
                      </td>
                    </tr>
                    <tr>
                      <td className="py-2 pr-2 font-medium text-blue-700">Immune System</td>
                      <td className="py-2">
                        Contributes to immunosenescence and "inflammaging"; higher cortisol:DHEA ratio in elderly linked to higher inflammatory markers and frailty
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              
              <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
                <h4 className="text-sm font-medium text-amber-800 mb-2">Optimal Cortisol Balance in Aging</h4>
                
                <div className="flex items-center justify-center mb-4">
                  <div className="relative w-[80%] h-6 bg-gradient-to-r from-red-400 via-green-400 to-red-400 rounded-full overflow-hidden">
                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 h-full w-[40%] border-4 border-white rounded-full"></div>
                  </div>
                </div>
                
                <div className="grid grid-cols-3 gap-2 text-center text-[10px] mb-3">
                  <div className="text-red-700">
                    <p className="font-medium">Too Low</p>
                    <p>Inadequate energy, poor stress response</p>
                  </div>
                  <div className="text-green-700">
                    <p className="font-medium">Optimal</p>
                    <p>Balanced rhythm with proper peaks and troughs</p>
                  </div>
                  <div className="text-red-700">
                    <p className="font-medium">Too High</p>
                    <p>Accelerated tissue aging, catabolism</p>
                  </div>
                </div>
                
                <p className="text-xs text-amber-800">
                  Managing cortisol is a critical part of managing aging. The goal is not complete suppression but rather an optimal range and pattern: adequate cortisol to maintain homeostasis while avoiding chronic excess that accelerates tissue deterioration.
                </p>
              </div>
            </div>
          </div>
        </>
      )
    },
    {
      id: 14,
      title: "Therapeutic Approaches for Cortisol Modulation",
      content: (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">Lifestyle & Behavioral Approaches</h3>
              
              <div className="grid grid-cols-1 gap-3">
                <div className="bg-white p-3 rounded-lg shadow-sm">
                  <div className="flex items-start">
                    <div className="mr-3 h-8 w-8 flex-shrink-0 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-lg text-blue-700">🧠</span>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-800 mb-1">Stress Management</h4>
                      <p className="text-xs text-gray-700">
                        Psychological stress is a potent activator of cortisol. Techniques like meditation, mindfulness, CBT, and biofeedback can normalize cortisol patterns. Even simple deep breathing or engaging in hobbies can reduce stress-induced cortisol elevation.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-3 rounded-lg shadow-sm">
                  <div className="flex items-start">
                    <div className="mr-3 h-8 w-8 flex-shrink-0 bg-indigo-100 rounded-full flex items-center justify-center">
                      <span className="text-lg text-indigo-700">💤</span>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-800 mb-1">Sleep Optimization</h4>
                      <p className="text-xs text-gray-700">
                        Cortisol has a circadian rhythm intimately linked with sleep-wake cycles. Sleep deprivation or erratic schedules elevate cortisol. Ensure 7-8 hours of quality sleep, maintain regular sleep timing, and create a cool, dark sleeping environment.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-3 rounded-lg shadow-sm">
                  <div className="flex items-start">
                    <div className="mr-3 h-8 w-8 flex-shrink-0 bg-amber-100 rounded-full flex items-center justify-center">
                      <span className="text-lg text-amber-700">🌞</span>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-800 mb-1">Circadian Rhythm Alignment</h4>
                      <p className="text-xs text-gray-700">
                        Morning bright light exposure and physical activity shortly after waking reinforce morning cortisol peak. Minimize bright screens and stress in late evening to allow proper cortisol decline before sleep.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-3 rounded-lg shadow-sm">
                  <div className="flex items-start">
                    <div className="mr-3 h-8 w-8 flex-shrink-0 bg-green-100 rounded-full flex items-center justify-center">
                      <span className="text-lg text-green-700">🏃</span>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-800 mb-1">Exercise (Goldilocks Effect)</h4>
                      <p className="text-xs text-gray-700">
                        Acute exercise raises cortisol, but habitual exercise lowers baseline levels. Overtraining elevates cortisol chronically, while moderate consistent training improves diurnal rhythm and increases anabolic hormones.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-4 bg-blue-50 p-3 rounded-md border border-blue-200">
                <h4 className="text-sm font-medium text-blue-800 mb-2">Diet & Metabolic Health</h4>
                <ul className="text-xs space-y-1.5 text-gray-700 list-disc list-inside">
                  <li>Maintain stable blood sugar to avoid hypoglycemic stress triggers</li>
                  <li>Extremely low-calorie or zero-carb diets may raise cortisol as a stress response</li>
                  <li>Balanced nutrition with adequate protein supports healthy cortisol regulation</li>
                  <li>Consider anti-inflammatory foods to counter cortisol's metabolic effects</li>
                  <li>Limit alcohol and caffeine, especially in the afternoon and evening</li>
                </ul>
              </div>
            </div>
            
            <div>
              <h3 className="text-lg font-medium text-blue-900 mb-3">Pharmacological & Supplemental Approaches</h3>
              
              <div className="bg-white p-4 rounded-lg shadow-md mb-4">
                <h4 className="text-sm font-medium text-gray-800 mb-3">Medical Interventions</h4>
                
                <div className="space-y-3 text-xs">
                  <div className="p-2 bg-gray-50 rounded-md">
                    <p className="font-medium text-gray-700 mb-1">Cortisol Replacement</p>
                    <p className="text-gray-600">
                      For adrenal insufficiency, oral hydrocortisone is given in divided doses (typically 15-25 mg total per day) to simulate natural diurnal rhythm. Modified-release preparations better mimic natural cortisol patterns.
                    </p>
                  </div>
                  
                  <div className="p-2 bg-gray-50 rounded-md">
                    <p className="font-medium text-gray-700 mb-1">Suppressing Excess Cortisol</p>
                    <p className="text-gray-600">
                      For Cushing's syndrome, medications like ketoconazole, metyrapone, or osilodrostat inhibit adrenal steroid synthesis. Mifepristone (RU-486) is a glucocorticoid receptor antagonist used in some cases.
                    </p>
                  </div>
                  
                  <div className="p-2 bg-gray-50 rounded-md">
                    <p className="font-medium text-gray-700 mb-1">Psychiatric Applications</p>
                    <p className="text-gray-600">
                      High cortisol is sometimes targeted in treatment-resistant depression. Experimental uses of cortisol-lowering agents or receptor blockers in PTSD and severe depression show variable results.
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h4 className="text-sm font-medium text-green-800 mb-3">Adaptogenic Herbs & Supplements</h4>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="bg-white p-3 rounded-md shadow-sm">
                    <div className="flex flex-col items-center mb-2">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center mb-1">
                        <span className="text-lg text-green-700">🌿</span>
                      </div>
                      <h5 className="text-xs font-medium text-gray-800">Ashwagandha</h5>
                    </div>
                    <ul className="text-[10px] space-y-1 list-disc list-inside text-gray-600">
                      <li>Reduces stress scores and cortisol levels</li>
                      <li>8-week RCT: extract (240mg/day) significantly reduced morning cortisol</li>
                      <li>Higher dose (600mg/day) showed 20-30% cortisol reduction</li>
                      <li>May work via central mechanisms affecting CRH</li>
                    </ul>
                  </div>
                  
                  <div className="bg-white p-3 rounded-md shadow-sm">
                    <div className="flex flex-col items-center mb-2">
                      <div className="h-10 w-10 rounded-full bg-pink-100 flex items-center justify-center mb-1">
                        <span className="text-lg text-pink-700">🌱</span>
                      </div>
                      <h5 className="text-xs font-medium text-gray-800">Rhodiola Rosea</h5>
                    </div>
                    <ul className="text-[10px] space-y-1 list-disc list-inside text-gray-600">
                      <li>Used for fatigue and stress resilience</li>
                      <li>Improves concentration in stress-related fatigue</li>
                      <li>Reduces cortisol awakening response after 4 weeks</li>
                      <li>Adaptogenic properties support stress hormone balance</li>
                    </ul>
                  </div>
                </div>
                
                <div className="mt-3 text-xs p-2 bg-white rounded-md">
                  <p className="font-medium text-gray-700 mb-1">Other Supplements:</p>
                  <ul className="space-y-1 list-disc list-inside text-gray-600">
                    <li>Phosphatidylserine (400-800mg) - Blunts cortisol responses to exercise and stress</li>
                    <li>DHEA - Has anti-glucocorticoid properties, may counteract age-related cortisol dominance</li>
                    <li>Magnesium - Supports stress response and HPA axis regulation</li>
                    <li>L-theanine - Promotes relaxation without sedation, may lower stress-induced cortisol</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6 bg-gradient-to-r from-blue-50 to-indigo-50 p-4 rounded-lg border border-blue-200">
            <h3 className="text-md font-medium text-blue-800 mb-3">Testosterone-Cortisol Balance Strategy</h3>
            
            <div className="grid grid-cols-3 gap-4">
              <div className="col-span-1">
                <div className="bg-white p-3 rounded-md shadow-sm h-full">
                  <h4 className="text-sm font-medium text-center text-gray-800 mb-2">The Catabolic-Anabolic Balance</h4>
                  
                  <div className="flex items-center justify-center mb-3">
                    <div className="h-16 w-16 rounded-full bg-red-100 border-2 border-red-300 flex items-center justify-center mx-2">
                      <div className="text-xs font-bold text-red-700 text-center">Cortisol</div>
                    </div>
                    <div className="text-xl text-gray-400 mx-2">⟷</div>
                    <div className="h-16 w-16 rounded-full bg-blue-100 border-2 border-blue-300 flex items-center justify-center mx-2">
                      <div className="text-xs font-bold text-blue-700 text-center">Testosterone</div>
                    </div>
                  </div>
                  
                  <p className="text-xs text-center text-gray-700">
                    Optimizing testosterone is a strategy to counteract cortisol's catabolic influence, especially in aging populations where T:C ratio naturally declines.
                  </p>
                </div>
              </div>
              
              <div className="col-span-2">
                <div className="bg-white p-3 rounded-md shadow-sm">
                  <h4 className="text-sm font-medium text-gray-800 mb-2">Clinical Application</h4>
                  
                  <p className="text-xs text-gray-700 mb-3">
                    Many patients present with a constellation of symptoms related to both hormones: abdominal obesity, fatigue, low-normal testosterone, and signs of insulin resistance. A comprehensive approach includes:
                  </p>
                  
                  <div className="grid grid-cols-2 gap-3 text-xs">
                    <div className="p-2 bg-blue-50 rounded-md">
                      <p className="font-medium text-blue-700 mb-1">Cortisol Management</p>
                      <ul className="list-disc list-inside text-gray-600 space-y-1">
                        <li>Stress reduction techniques</li>
                        <li>Sleep optimization</li>
                        <li>Circadian rhythm alignment</li>
                        <li>Anti-inflammatory diet</li>
                        <li>Targeted adaptogenic support</li>
                      </ul>
                    </div>
                    
                    <div className="p-2 bg-green-50 rounded-md">
                      <p className="font-medium text-green-700 mb-1">Testosterone Support</p>
                      <ul className="list-disc list-inside text-gray-600 space-y-1">
                        <li>Resistance training</li>
                        <li>Adequate protein intake</li>
                        <li>Zinc and vitamin D optimization</li>
                        <li>Weight management</li>
                        <li>Medical replacement when indicated</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-3 text-xs text-gray-700 bg-white p-2 rounded-md">
              <p className="italic text-center">
                "Optimizing the cortisol-testosterone axis represents a fundamental strategy in longevity medicine, counteracting the progressive shift toward catabolism that characterizes aging and stress-related conditions."
              </p>
            </div>
          </div>
        </>
      )
    },
    {
      id: 15,
      title: "Actionable Protocols for Clinicians",
      content: (
        <>
          <div className="overflow-y-auto" style={{ maxHeight: '65vh', scrollbarWidth: 'thin' }}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-medium text-blue-900 mb-3">Testing & Interpretation Guide</h3>
                
                <div className="bg-white p-4 rounded-lg shadow-md mb-4">
                  <div className="flex items-start space-x-3 mb-4">
                    <div className="flex-shrink-0 h-7 w-7 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-700 text-lg font-semibold">1</span>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-800">Clinical Assessment First</h4>
                      <ul className="mt-1 text-xs text-gray-700 space-y-1 list-disc list-inside pl-1">
                        <li>History for high cortisol: central weight gain, muscle weakness, purple striae, hypertension</li>
                        <li>Low cortisol signs: fatigue, weight loss, hypotension, hyperpigmentation, salt craving</li>
                        <li>Low testosterone: fatigue, reduced libido, muscle loss, depression (in men)</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3 mb-4">
                    <div className="flex-shrink-0 h-7 w-7 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-700 text-lg font-semibold">2</span>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-800">Basic Screening Labs</h4>
                      <ul className="mt-1 text-xs text-gray-700 space-y-1 list-disc list-inside pl-1">
                        <li>8 AM serum cortisol (and ACTH if suspect adrenal disease)</li>
                        <li>Adrenal insufficiency: 8 AM cortisol &lt;3 μg/dL strongly suggestive</li>
                        <li>Cushing's: 8 AM cortisol &gt;18 μg/dL warrants further investigation</li>
                        <li>DST: 1 mg dexamethasone at 11 PM, measure cortisol next morning</li>
                        <li>Late-night salivary cortisol for cyclic or stress-related issues</li>
                      </ul>
                    </div>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0 h-7 w-7 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-blue-700 text-lg font-semibold">3</span>
                    </div>
                    <div>
                      <h4 className="text-sm font-medium text-gray-800">Include Related Hormones</h4>
                      <ul className="mt-1 text-xs text-gray-700 space-y-1 list-disc list-inside pl-1">
                        <li>Check plasma ACTH with any abnormal cortisol to help localize issue</li>
                        <li>Measure DHEA-S (low in adrenal insufficiency, often high-normal in Cushing's)</li>
                        <li>In men: morning total testosterone (may be low when cortisol is high)</li>
                        <li>In women: estradiol and progesterone if menstrual changes occur</li>
                      </ul>
                    </div>
                  </div>
                </div>
                
                <div className="bg-purple-50 p-3 rounded-lg border border-purple-200">
                  <h4 className="text-sm font-medium text-purple-800 mb-2">Advanced Test Interpretations</h4>
                  <div className="space-y-3 text-xs">
                    <div className="bg-white p-2 rounded-md">
                      <p className="font-medium text-purple-700">Cortisol Awakening Response (CAR)</p>
                      <div className="flex justify-between items-center mt-1 text-gray-600">
                        <div className="text-center">
                          <p className="font-medium">Normal</p>
                          <p>50-160% increase from baseline</p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium">Blunted</p>
                          <p>&lt;50% increase</p>
                          <p className="text-[9px] italic">(Chronic stress, exhaustion)</p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium">Exaggerated</p>
                          <p>&gt;160% increase</p>
                          <p className="text-[9px] italic">(Acute anxiety, depression)</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-white p-2 rounded-md">
                      <p className="font-medium text-purple-700">Diurnal Rhythm Pattern</p>
                      <p className="text-gray-600 mb-1">Four salivary measurements across the day:</p>
                      <div className="flex justify-between text-gray-600">
                        <div className="text-center">
                          <p className="font-medium">Morning</p>
                          <p>Highest</p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium">Noon</p>
                          <p>~50% of morning</p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium">Evening</p>
                          <p>~30% of morning</p>
                        </div>
                        <div className="text-center">
                          <p className="font-medium">Midnight</p>
                          <p>&lt;20% of morning</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-medium text-blue-900 mb-3">Therapeutic Interventions</h3>
                
                <div className="grid grid-cols-1 gap-4">
                  <div className="bg-white p-4 rounded-lg shadow-md">
                    <h4 className="text-sm font-medium text-gray-800 mb-2">Cortisol Replacement Dosing</h4>
                    <table className="w-full text-xs">
                      <thead>
                        <tr>
                          <th className="py-1 text-left font-medium text-gray-700 border-b">Condition</th>
                          <th className="py-1 text-left font-medium text-gray-700 border-b">Dosing Strategy</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td className="py-2 pr-2 border-b border-gray-100 font-medium text-blue-700">Adrenal Insufficiency</td>
                          <td className="py-2 border-b border-gray-100">
                            Total daily: 15-25mg hydrocortisone<br/>
                            Common regimen: 20mg on waking, 10mg at 3-4PM (⅔ AM, ⅓ PM)<br/>
                            Alternative: 15mg AM, 5mg late PM for milder cases
                          </td>
                        </tr>
                        <tr>
                          <td className="py-2 pr-2 border-b border-gray-100 font-medium text-blue-700">Cushing's Syndrome<br/>(Medical Tx)</td>
                          <td className="py-2 border-b border-gray-100">
                            Ketoconazole: 200mg BID initially, up to 400mg TID<br/>
                            Metyrapone: 250mg 2-3x/day, up to 4g/day<br/>
                            Osilodrostat: starting 2mg BID<br/>
                            <span className="text-red-600 text-[10px]">*Requires specialist monitoring</span>
                          </td>
                        </tr>
                        <tr>
                          <td className="py-2 pr-2 font-medium text-blue-700">Adaptogens</td>
                          <td className="py-2">
                            Ashwagandha: 300-600mg standardized extract daily<br/>
                            Rhodiola: 200-400mg extract (3% rosavins) daily<br/>
                            Phosphatidylserine: 400-800mg at night
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                  
                  <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 mb-3">
                    <h4 className="text-sm font-medium text-amber-800 mb-2">Testosterone Therapy Guidelines</h4>
                    <ul className="text-xs space-y-1.5 text-gray-700 list-disc list-inside">
                      <li>For men with clinical hypogonadism + appropriate labs</li>
                      <li>IM injections: Testosterone cypionate 100mg weekly or 50mg twice weekly</li>
                      <li>Transdermal: 50-100mg gel daily</li>
                      <li>Target: Mid-normal youthful levels (500-800 ng/dL range)</li>
                      <li>Start conservatively in older men (75mg/week)</li>
                      <li>Monitor: levels, symptoms, PSA, hematocrit</li>
                    </ul>
                  </div>
                </div>
                
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <h4 className="text-sm font-medium text-green-800 mb-2">Integrated Protocol Framework</h4>
                  
                  <ol className="space-y-3 text-xs">
                    <li className="flex items-start">
                      <div className="mr-2 h-5 w-5 flex-shrink-0 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-700 text-xs font-semibold">1</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700">Assessment Phase</p>
                        <p className="text-gray-600">Comprehensive evaluation (history, physical, labs). Educate patient on stress hormones' impact on healthspan. Set goals (energy, sleep, body composition).</p>
                      </div>
                    </li>
                    
                    <li className="flex items-start">
                      <div className="mr-2 h-5 w-5 flex-shrink-0 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-700 text-xs font-semibold">2</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700">Lifestyle Implementation</p>
                        <p className="text-gray-600">First-line approach: "Circadian Reset" program with regular sleep, morning light, and meal timing. Add stress reduction techniques and appropriate physical activity.</p>
                      </div>
                    </li>
                    
                    <li className="flex items-start">
                      <div className="mr-2 h-5 w-5 flex-shrink-0 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-700 text-xs font-semibold">3</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700">Reevaluate & Add Supplements</p>
                        <p className="text-gray-600">After 8-12 weeks, reassess progress. Many improve with lifestyle alone. For residual issues, consider targeted adaptogens based on symptoms.</p>
                      </div>
                    </li>
                    
                    <li className="flex items-start">
                      <div className="mr-2 h-5 w-5 flex-shrink-0 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-700 text-xs font-semibold">4</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700">Hormone Therapy When Indicated</p>
                        <p className="text-gray-600">For diagnosed deficiencies, provide replacement therapy. Always treat adrenal insufficiency before testosterone to avoid adrenal crisis.</p>
                      </div>
                    </li>
                    
                    <li className="flex items-start">
                      <div className="mr-2 h-5 w-5 flex-shrink-0 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-700 text-xs font-semibold">5</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-700">Monitor & Personalize</p>
                        <p className="text-gray-600">Regular follow-up to adjust therapy based on clinical response and lab values. Maintain focus on lifestyle as foundation for long-term hormonal health.</p>
                      </div>
                    </li>
                  </ol>
                </div>
              </div>
            </div>
            
            <div className="mt-6 text-center">
              <p className="text-sm italic text-gray-700">
                "A clinician managing cortisol and testosterone must orchestrate lifestyle changes, supplements, and medications to maintain balance between catabolic and anabolic signals, improving both quality of life and potential healthspan."
              </p>
            </div>
          </div>
        </>
      )
    }
  ];


  return (
    <MentorshipAccessGate>
    <div className="min-h-screen flex flex-col">
      <Navbar />
      
      <div className="container mx-auto px-4 py-8 flex-grow max-w-5xl">
        <Breadcrumb className="mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship">Mentorship</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink href="/mentorship/lectures">Lectures</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink>Cortisol: Molecular Foundations</BreadcrumbLink>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        
        <div className="flex justify-between items-center mb-6">
          <Button 
            variant="ghost" 
            className="flex items-center text-[#3498DB]"
            onClick={() => navigate("/mentorship")}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Mentorship
          </Button>
          
          <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
            Lecture Material
          </div>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="md:col-span-2 space-y-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-serif font-bold text-[#2C3E50] mb-4">
                Cortisol: Molecular Foundations
              </h1>
              <p className="text-[#7F8C8D] mb-6">
                A comprehensive exploration of cortisol's molecular structure, biosynthesis, 
                signaling pathways, and implications for longevity medicine.
              </p>
              <div className="flex items-center gap-3 mb-8">
                <div className="flex items-center text-sm text-[#7F8C8D]">
                  <FileText className="h-4 w-4 mr-1" />
                  <span>Lecture Duration: 45 minutes</span>
                </div>
                <div className="w-1 h-1 rounded-full bg-gray-300"></div>
                <div className="flex items-center text-sm text-[#7F8C8D]">
                  <Book className="h-4 w-4 mr-1" />
                  <span>Last Updated: April 10, 2025</span>
                </div>
              </div>
            </div>
            
            <Separator />
            
            {/* Slideshow */}
            <Slideshow slides={lectureSlides} />
            
            {/* Video placeholder */}
            <VideoPlayer />
          </div>
          
          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="sticky top-20">
              <Card>
                <CardHeader>
                  <CardTitle>Lecture Resources</CardTitle>
                  <CardDescription>
                    Additional materials to enhance your learning
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    <div className="bg-blue-50 rounded-md p-3 flex items-start">
                      <FileText className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-blue-800">Lecture Slides</h4>
                        <p className="text-sm text-blue-600">
                          Complete slide deck from the presentation
                        </p>
                        <button className="text-xs text-blue-700 mt-1 hover:underline">
                          Download PDF
                        </button>
                      </div>
                    </div>
                    
                    <div className="bg-green-50 rounded-md p-3 flex items-start">
                      <FileText className="h-5 w-5 text-green-600 mr-2 mt-0.5" />
                      <div>
                        <h4 className="font-medium text-green-800">Reference Guide</h4>
                        <p className="text-sm text-green-600">
                          Quick reference for cortisol pathways
                        </p>
                        <button className="text-xs text-green-700 mt-1 hover:underline">
                          Download PDF
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <div className="mt-6 bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-md">
                <div className="flex">
                  <Info className="h-5 w-5 text-amber-500 mr-2 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-amber-800 mb-1">Next Live Session</h4>
                    <p className="text-sm text-amber-700">
                      Join our follow-up discussion on May 5, 2025 at 7:00 PM EST
                      for a Q&A session on cortisol and stress management.
                    </p>
                    <button className="text-xs font-medium text-amber-800 mt-2 hover:underline">
                      Add to Calendar
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
    </MentorshipAccessGate>
  );
}